import { wallHuntGameAttack } from './awoken_wall_hunt_game'
// ボス攻撃のデータ定義。
// 設計のキモ (roster-master §3.1):
//   - kind = 見た目ラベル (描画/SE 用)。 防御の正解は defense (parry/evade-counter/evade) が持つ。
//   - defense 未指定の攻撃は parryable から導出 (後方互換)。 新ボスは defense を明示する。
//   - 配役の正本 = docs/departments/scenario/cast-roster-master.md。
//     旧2体 (ハーピー/曲芸師) は退役済。 現状の実装ボスは「王 (dream-hound)」のみ。

import type { AttackDef } from './types'
import { awokenRushStudyAttack } from './awoken_rush_study'
import { PLAYER_DAMAGE } from './balance'
import { CHOREO_CHARGE_MS, CHOREO_ACTIVE_MS } from './awoken_rampage_choreo'
import { ROOSTER_ROAM_CHARGE_MS } from './rooster_aerial_roam'
import { ROOSTER_DAWN_CHARGE_MS, ROOSTER_DROP_CHARGE_MS, ROOSTER_FINALE_CHARGE_MS, roosterFeatherAttack } from './rooster_vertical'
import { FAJIN_RECOVERY_MS } from './executioner_fajin_motion'
import { KNIGHT_SHOVE_PUSH_PX } from './knight_spacing'
import { TOKI_LONG_CHARGE_MS, TOKI_LONG_GAPS_MS, TOKI_SHORT_CHARGE_MS, TOKI_SHORT_GAP_MS } from './rooster_toki'

// 腕砲3技の砲口。予告FX・水平弾・榴弾の見た目玉がすべてここを読む。
// 発射位置と溜めの光を別々に調整してズラさないため、攻撃ごとの複製は禁止。
// 🔵 2026-08-27 夜: ~~上72px~~ は膝の高さで、実プレイで「弾が膝のあたりから生成される」と
//   読まれた (検収パック 2026-08-27 夜)。絵の砲口を実測して直した:
//   構え (aim_hold) = 上197px/前76px、発射 (fire_recoil) = 砲身を振り下ろすので 上99px/前68px。
//   ポーズごとに砲口の高さが本当に違うため、アンカーを2つに分ける (telegraph 中=AIM / それ以外=下の値)。
//   測り方 = cannon/*.png の alpha bbox と最左端列の縦中心 (scratchpad measure_muzzle.py・実機換算 236.3/1354)。
// 🔵 2026-09-02 深夜 斜め下撃ちの絵 (fire_down_a) へ。fix 1.15・192px 実測
export const EXECUTIONER_CANNON_MUZZLE = {
  spawnForwardPx: 92,
  spawnOffsetY: 98,
} as const
// 構え (aim_hold) 中の砲口。溜めの岩・予告FXは render の executionerCannonMuzzle が
// telegraph 中だけこちらを読む。🔴 弾の発生 (castProjectile) には使わない。
// 🔵 2026-09-02 深夜 斜め下撃ちの絵 (aim_down_a) へ。fix 1.15・192px 実測。砲口＝背丈の 45%
export const EXECUTIONER_CANNON_MUZZLE_AIM = {
  spawnForwardPx: 86,
  spawnOffsetY: 87,
} as const
// 榴弾ボム (exec_cannonMortar) の出発点。mortar_aim・fix 1.04・192px 実測
export const EXECUTIONER_CANNON_MUZZLE_MORTAR = {
  spawnForwardPx: 62,
  spawnOffsetY: 176,
} as const

// 発勁の突き (exec_bladeFajin・刃形態限定) の出発点＝突き出した刃先。
//   🟨 仮値 (2026-09-07 便1・骨だけ)。目安=腰の高さ・前方へ寄せる。ラボで見て後から合わせる。
//   突き出し (pierce_thrust) の pose offset (+30) に乗る前提の値＝据えの突き
//   (exec_maulSetPierce・range 168) の刃先到達距離から逆算した近似。EXECUTIONER_CANNON_MUZZLE* とは
//   別腹 (刃形態専用の新設)。砲形態の定数は書き換えない。
//   正本 = docs/candidates/2026-09-07-executioner-fajin-thrust-motion-spec.md §6
export const EXECUTIONER_BLADE_FAJIN_MUZZLE = {
  spawnForwardPx: 140,
  spawnOffsetY: 90,
} as const

// 戴冠獣の三連 (hound-triple) の3段の踏み込み (px)。2026-10-08 ユーザー採用 (検収パック H-4「こちらが良い」) = 配布版の既定。
//   予備動作の直後から下がり続ける主人公にも届かせる。2段目は間が短いので浅く。
//   旧 (踏み込みなし) は DEV の ?houndTripleChase=off (src/hound_triple_chase.ts)。
export const HOUND_TRIPLE_CHASE_LUNGE_PX = {
  houndTripleSwipe1: 100,
  houndTripleSwipe2: 60,
  houndTripleSlam: 80,
} as const

export const ATTACKS: Record<string, AttackDef> = {
  // ========== 名も無き亡霊 (nameless-shade・導入戦) ==========
  // 正典 §7.0: 稽古の相手 (チュートリアル)。 物語の役どころは付けない (台帳 story.md 47回目)。
  // 新3文法 (hit-timeline 2026-06-11): ①弾き ②短い避け→反撃 ③長い位置どり を
  // 4攻撃で一通り、 遅く・優しく出題する。
  shadeSlash: {
    id: 'shadeSlash',
    label: '亡霊の斬り',
    kind: 'physical',
    chargeMs: 1200,
    activeMs: 160,
    recoveryMs: 900,
    parryable: true,
    range: 86,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。旧 130 は ?bossReach=old。
    // 背面カバー (2026-07-23・thrust/grab 2026-07-22 裁定Aと同じ理由): 踏み込みの最低量
    //   (lungeDistance×minLungeFrac) が強制前進のため、 密着すると跨いで判定の外 (背中側) へ
    //   抜けてしまい、 空振り→跨いでは空振りのループに入る (棒立ち検証で34攻撃中10空振り=29%)。
    //   後ろ側へ伸ばして「跨いでも判定の中」にする。 外側の到達距離 (localToPx=間合い) は不変。
    //   検査 = tests/combat_sim/shade_whiff.test.ts
    strike: { localFromPx: -60, localToPx: 86 },
    approachDistancePx: 86,
    contactAtActiveStart: true, // custom draw: active開始で slash_contact を表示
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 22,
    crossingPolicy: 'track-until-commit',
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 120,
    lungeDistance: 110,
    sourceBossId: 'nameless-shade',
  },
  // 短い避け→反撃: 窓 180ms ≤ i-frame(200)-10 = 回避1回で丸ごと跨いで、 直後に反撃が通る
  shadeThrust: {
    id: 'shadeThrust',
    label: '亡霊の突き',
    kind: 'physical',
    chargeMs: 1200,
    activeMs: 180,
    recoveryMs: 950,
    parryable: false,
    unblockable: true,   // 裁定C: 突き=弾けない=赤
    range: 99,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。旧 140 は ?bossReach=old。
    // 背面カバー (2026-07-22 ユーザー裁定A): 判定が前方だけだと、 回避で懐へ入った/抜けた瞬間に
    //   判定の外へ出て **タイミングが合っていても見切りが開かない** (hits.ts は overlapsPlayer の
    //   内側でしか counterWindow を開かない)。 実測 = 立ち位置x時刻x向き 180通り中 33〜51%しか
    //   見切りが取れていなかった。 後ろ側へ伸ばして「抜けても判定の中」にし、 見切りを
    //   *位置ゲー* から *タイミングゲー* へ戻す。 外側の到達距離 (localToPx) は不変 = 間合いは変えない。
    //   ※ 背面カバー自体は既存語彙 (veilSweep -120 / houndLunge -45 等)。 新機構ではない。
    //   検査 = tests/combat_sim/dodge_counter_audit.test.ts
    strike: { localFromPx: -60, localToPx: 99 },
    approachDistancePx: 99,
    contactAtActiveStart: true, // custom draw: active開始で thrust_contact を表示
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 22,
    crossingPolicy: 'track-until-commit',
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    lungeDistance: 230,
    sourceBossId: 'nameless-shade',
  },
  // 掴み = 反撃できる + リーチ大きめ (spec 2026-06-11 hit-timeline §5)。
  //   両腕の掴みを避けた = 空振り = 丸腰 → 反撃が通る。 リスク=範囲広 / リターン=綺麗に反撃。
  shadeGrab: {
    id: 'shadeGrab',
    label: '亡霊の掴み',
    kind: 'physical',
    chargeMs: 1100,
    activeMs: 180,
    recoveryMs: 950,
    parryable: false,
    unblockable: true,   // 裁定C: 掴み=弾けない=赤
    range: 84,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。旧 170 は ?bossReach=old。
    // 背面カバー (2026-07-22 ユーザー裁定A): 判定が前方だけだと、 回避で懐へ入った/抜けた瞬間に
    //   判定の外へ出て **タイミングが合っていても見切りが開かない** (hits.ts は overlapsPlayer の
    //   内側でしか counterWindow を開かない)。 実測 = 立ち位置x時刻x向き 180通り中 33〜51%しか
    //   見切りが取れていなかった。 後ろ側へ伸ばして「抜けても判定の中」にし、 見切りを
    //   *位置ゲー* から *タイミングゲー* へ戻す。 外側の到達距離 (localToPx) は不変 = 間合いは変えない。
    //   ※ 背面カバー自体は既存語彙 (veilSweep -120 / houndLunge -45 等)。 新機構ではない。
    //   検査 = tests/combat_sim/dodge_counter_audit.test.ts
    strike: { localFromPx: -60, localToPx: 84 },
    approachDistancePx: 84,
    contactAtActiveStart: true, // custom draw: active開始で grab_contact を表示
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 26,
    crossingPolicy: 'track-until-commit',
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    lungeDistance: 100,
    sourceBossId: 'nameless-shade',
  },
  // ③ 亡霊の波 — 長い位置どりの教室 (hit-timeline §2)。 前方の床が長く揺らぐ。
  //   持続 600ms ≫ i-frame 200ms = タイミングでは抜けられない。歩いて印の外へ、を最初に教える。
  shadeRipple: {
    id: 'shadeRipple',
    label: '亡霊の波',
    kind: 'area',
    chargeMs: 1400,
    activeMs: 600,
    recoveryMs: 1100,
    parryable: false,
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 22,
    sourceBossId: 'nameless-shade',
    // 帯=[亡霊+60..亡霊+240]。 内側60px = 懐は安全 (前へ抜けても良い)。
    //   予告1400ms: 最悪位置からの脱出 ~105px ≤ 移動上限 ~193px (hazard_escape が自動検査)
    hazardSpawns: [{
      anchor: 'boss', offset: 150, width: 180, telegraphMs: 1400, activeMs: 600,
      damage: PLAYER_DAMAGE.standard, postureDamage: 22, hitOnce: true, kind: 'shock',
    }],
  },
  // 配布版の稽古の「崩されて弾く」課題で出す2技 (src/tut_focus.ts SHADE_COMBO3_LESSONS)。
  //   DEV ?tutFocus=1|2|3 でも使う。旧課題 (DEV ?tutFocus=0)・仕上げ・通常戦闘の baseAttackIds には含めない。
  //   設計 = docs/research/2026-10-11-exec-parry-tutorial-design.md §2・§5。
  // 三連の斬り: 1発ずつは斬りと同じ見た目・同じく弾ける。ガードで受け続けると3発目で崩れる (guard.ts の決まりのまま)。
  //   段の間 520ms = 受けた後の体幹が戻らない 2.5 秒の中に3発が収まる。絵は斬りの4枚を使い回す (render.ts)。
  shadeGuardChain: {
    id: 'shadeGuardChain',
    label: '亡霊の三連斬り',
    kind: 'physical',
    chargeMs: 1200,
    activeMs: 160,
    recoveryMs: 900,
    comboHits: 3,
    comboGapsMs: [520, 520],
    comboGapMs: 520,
    parryable: true,
    range: 86,
    strike: { localFromPx: -60, localToPx: 86 },
    approachDistancePx: 86,
    contactAtActiveStart: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 22,
    crossingPolicy: 'track-until-commit',
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 120,
    lungeDistance: 110,
    sourceBossId: 'nameless-shade',
  },
  // 決め技: 崩れた主人公へだけ出す。紫のオーラは致命の決まりで自動に付く (render.ts auraStrength)。
  //   溜め 900ms = 本番 (450〜688ms) より長い (設計案の仮値)。傷は普段の一発と同じ・体力は 1 で止まる (protectFromDeath)。
  shadeExecution: {
    id: 'shadeExecution',
    label: '亡霊の決め技',
    kind: 'physical',
    chargeMs: 900,
    activeMs: 160,
    recoveryMs: 900,
    parryable: false,
    range: 86,
    strike: { localFromPx: -60, localToPx: 86 },
    approachDistancePx: 86,
    contactAtActiveStart: true,
    damage: PLAYER_DAMAGE.standard,
    bossFinisher: true,
    postureDamage: 0,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'boss-finisher',
    sourceBossId: 'nameless-shade',
  },

  // ドリームハウンド ― 体で攻める獣。すべて physical = parry
  houndBite: {
    id: 'houndBite',
    label: '噛みつき',
    kind: 'physical',
    // 650: 溜めが長いと凍結中の後退で巨大ギャップが開き、 active 内に詰め切れずワープ化する。
    //   短め溜め + ホーミング突進 + 通常間合い詰めで「後退しても自然に届く」を成立させる。
    chargeMs: 650,
    activeMs: 140,
    recoveryMs: 560,
    parryable: true,
    // 2026-10-07 実測: houndBite_contact の頭前端 (王冠) は中心から 144px。絵に当たりを合わせる。
    range: 145,
    // 獣の胴体接触から口先まで。背面は含めない。
    // 後方 -15 (2026-09-18 身体判定なし採用): 中心の背中側 ±24 (向き直りの不感帯) に立たれても牙が届くようにする。
    //   出所 = tests/ghost_body_trial.test.ts「不感帯の中に穴なし」。
    strike: { localFromPx: -15, localToPx: 145 },
    // 当たりだけ 15px 延長し、立ち位置・踏み込みの目標は従来の 130px のまま。
    approachDistancePx: 130,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 42,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130,
    crossingPolicy: 'track-until-commit',
    // 踏み込み噛み: 静止噛みではなく前へ一歩踏み込んで噛む。
    //   控えめ (140) = 突進牙(260)より短い。 後退していても踏み込みで届かせる役割。
    //   active 開始時にプレイヤーの range-buffer 手前を狙う (位置避け不可)。
    lungeDistance: 140,
    sourceBossId: 'dream-hound',
  },
  houndLunge: {
    id: 'houndLunge',
    label: '突進牙',
    kind: 'physical',
    chargeMs: 900,
    // 180 (hit-timeline 2026-06-11): 回避1回 (i-frame 200) で窓を丸ごと跨げる上限。
    //   旧240はヒットが持続する新モデルだと回避し切れない。 突進の見た目の勢いは
    //   lungeSpeed (ホーミング上限 1900px/s) が担うので、 窓を縮めても踏み込みは届く。
    activeMs: 180,
    recoveryMs: 620,
    parryable: true,
    // range 140: 突進牙の先端がプレイヤーの最小衝突距離(108)を確実に超えて届くよう拡張。
    //   active の踏み込み (lunge 260) がプレイヤーの range-buffer 手前へ着地 →
    //   後退しても突進が届く (位置避け不可)。 間合い詰めは telegraph ではなくこの踏み込みで行う。
    range: 140,
    strike: { localFromPx: 0, localToPx: 145 },
    approachDistancePx: 140,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 45,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 180,
    crossingPolicy: 'track-until-commit',
    lungeDistance: 260,
    // 溜め終端で向きをコミットし、active はプレイヤー位置へ着地せずこの方向へ260px走り抜ける。
    positionEvadable: true,
    // contact姿勢(25%=45ms)を最初のfixed tickで評価し終えるまで位置を保ち、
    // 至近距離の相手を判定前に追い越さない。その後は約36px/tick以下で走り切る。
    forwardWarp: [0, 0, 0, 0, 1 / 6, 1 / 3, 1 / 2, 2 / 3, 5 / 6, 1],
    stopMovementOnParry: true,
    requiresForwardCenter: true,
    sourceBossId: 'dream-hound',
  },

  // 駆け抜け (2026-10-10・比較版 DEV ?houndPassThrough=1・src/hound_pass_through.ts)。主人公の脇を駆け抜け、
  //   向こう側で滑って止まり、待機で振り返る。当たりは無い (range 0 = ボス追従の当たりを出さない・理由は hound_pass_through.ts)。
  //   出るのは boss.ts の待機の抽選の前の差し込みだけ (連の一覧・baseAttackIds には入れない)。
  //   activeMs は走る距離で毎回決める (boss.ts が active に入る時に上書き・ここは控え)。位置は boss.ts が直接置く
  //   (踏み込みの「芯の手前で止まる」を通らない) ので、体は主人公を素通りする。
  houndPassRun: {
    id: 'houndPassRun', label: '駆け抜け', kind: 'physical',
    chargeMs: 600, activeMs: 400, recoveryMs: 750,
    parryable: false, range: 0, damage: 0, postureDamage: 0,
    swapsSides: true, locksFacingDuringRecovery: true,
    crossingPolicy: 'intentional-opening',
    sourceBossId: 'dream-hound',
  },

  // v2 proxy kit, selected only by the resolved dream-hound definition.
  houndPounce: {
    id: 'houndPounce', label: 'のしかかり', kind: 'physical',
    // 1100ms tall hold + 320ms render-only hop, then grounded contact.
    // Hop 160->320ms (10-06 user: 'a little tight'): a dodge press after the hop starts still lands in the 200ms i-frame window.
    chargeMs: 1420, activeMs: 200, recoveryMs: 1100,
    parryable: false, unblockable: true, range: 145,
    // Same back-side coverage contract as shadeThrust/shadeGrab.
    strike: { localFromPx: -60, localToPx: 145 }, approachDistancePx: 145,
    damage: PLAYER_DAMAGE.standard, postureDamage: 45,
    turnsDuringTelegraph: true, facingLockBeforeActiveMs: 160,
    // 10-07 user: 「着地後に突進してない？」 = 体が着地の絵のまま active の 200ms で前へ滑っていた。
    //   滑りを跳びの 320ms (空中の絵) へ移し、着地した所で当たる (lungeDistance は持たない = active では動かない)。
    //   preActiveLunge = ease-in で最後に最も速く詰まる (跳びかかる勢い)。主人公との間が 100px 以下なら詰めない。
    crossingPolicy: 'track-until-commit',
    preActiveLungeMs: 320, preActiveLungeDistance: 220, preActiveLungeStopGapPx: 100,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndSwipe: {
    id: 'houndSwipe', label: '前脚の払い', kind: 'physical',
    chargeMs: 600, activeMs: 150, recoveryMs: 650,
    // 10-08 contact art: alpha>16 area=496470, striking tip source x=4;
    // bbox-centred, floor-anchored, scale=186*0.89/sqrt(area): paw reach ~184px.
    range: 184, strike: { localFromPx: 0, localToPx: 184 }, approachDistancePx: 169,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 40,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    // Base 100px slide with bite's catch-up: the first boss has no position-dodge moves.
    lungeDistance: 100,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndTripleSwipe1: {
    id: 'houndTripleSwipe1', label: '三連・払い', kind: 'physical',
    chargeMs: 750, activeMs: 130, recoveryMs: 250,
    // 10-08 contact art: alpha>16 area=544908, striking tip source x=4;
    // bbox-centred, floor-anchored, scale=186*0.89/sqrt(area): paw reach ~171px.
    range: 171, strike: { localFromPx: 0, localToPx: 171 }, approachDistancePx: 156,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 35,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    lungeDistance: HOUND_TRIPLE_CHASE_LUNGE_PX.houndTripleSwipe1,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndTripleSwipe2: {
    id: 'houndTripleSwipe2', label: '三連・返し', kind: 'physical',
    chargeMs: 250, activeMs: 130, recoveryMs: 250,
    // 10-08 contact art: alpha>16 area=583678, striking tip source x=4;
    // bbox-centred, floor-anchored, scale=186*0.89/sqrt(area): paw reach ~161px.
    range: 161, strike: { localFromPx: 0, localToPx: 161 }, approachDistancePx: 146,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 35,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    lungeDistance: HOUND_TRIPLE_CHASE_LUNGE_PX.houndTripleSwipe2,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndTripleSlam: {
    id: 'houndTripleSlam', label: '三連・叩きつけ', kind: 'physical',
    chargeMs: 700, activeMs: 180, recoveryMs: 800,
    // 10-08 contact art: alpha>16 area=562587, striking tip source x=40;
    // bbox-centred, floor-anchored, scale=186*0.89/sqrt(area): paw reach ~158px.
    range: 158, strike: { localFromPx: 0, localToPx: 158 }, approachDistancePx: 143,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 50,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    lungeDistance: HOUND_TRIPLE_CHASE_LUNGE_PX.houndTripleSlam,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndTailSweep: {
    id: 'houndTailSweep', label: '尾の薙ぎ', kind: 'physical',
    chargeMs: 800, activeMs: 180, recoveryMs: 700,
    // 10-08 contact art: alpha>16 area=446632, striking tip source x=1534;
    // bbox-centred, floor-anchored, scale=186*0.89/sqrt(area): rear tail reach ~190px.
    range: 190, strike: { localFromPx: -190, localToPx: -20 }, approachDistancePx: 175,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 40,
    turnsDuringTelegraph: false, locksFacingDuringRecovery: true, crossingPolicy: 'rear-coverage',
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  // 幕2の三連 (新・2026-10-08 ユーザー「3連攻撃は一回取り下げたけど2幕に入れてほしい／ただし、読みにくい技だったので
  //   他のモーションなどで作った方が良い。作りやすい技でいいよ。タイミングは同じが良い」)。新しい絵は描かず、今ある絵を使い回す。
  //   爪 (奥の前脚) → 牙 (頭) → 両前脚を振り上げて手前の前脚で叩きつけ。打つ部位が毎回違う (同じ脚を続けて上げない)。
  //   拍は三連の描き直し版と同じ (当たりから当たり 830ms・1280ms・src/hound_triple_redraw.ts)。踏み込みは三連と同じ 100/60/80。
  //   絵・並び・選んだ理由 = docs/research/2026-10-08-hound-hp-posture-tune.md §6。連 = src/hound_kit.ts hound-triple2。
  houndTriple2Claw: {
    id: 'houndTriple2Claw', label: '三連・爪', kind: 'physical',
    chargeMs: 750, activeMs: 130, recoveryMs: 250,
    // 当たりの絵 houndTriple_swipeFar (奥の前脚の爪先・描き直し版の払い2と同じ絵＝170)。
    range: 170, strike: { localFromPx: 0, localToPx: 170 }, approachDistancePx: 155,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 35,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    lungeDistance: 100,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndTriple2Fang: {
    id: 'houndTriple2Fang', label: '三連・牙', kind: 'physical',
    chargeMs: 650, activeMs: 130, recoveryMs: 250,
    // 当たりの絵 houndBite_contact (単発の噛みと同じ絵＝頭の前端 144 → 145・背中側 -15 も噛みと同じ)。
    range: 145, strike: { localFromPx: -15, localToPx: 145 }, approachDistancePx: 130,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 35,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    lungeDistance: 60,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndTriple2Slam: {
    id: 'houndTriple2Slam', label: '三連・叩きつけ', kind: 'physical',
    chargeMs: 1100, activeMs: 180, recoveryMs: 800,
    // 当たりの絵 houndTriple_slam (手前の前脚・旧三連の叩きつけと同じ絵＝158)。
    range: 158, strike: { localFromPx: 0, localToPx: 158 }, approachDistancePx: 143,
    parryable: true, damage: PLAYER_DAMAGE.standard, postureDamage: 50,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    lungeDistance: 80,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  // 2026-10-08 ユーザー判断で戴冠獣の技から外した (検収 H-1「掴みじゃなくて突進に見える・当たりも動きもおかしい・なくしても良い」)。
  //   定義・絵・タイムラインは残す。通常の戦いでは出ない (src/hound_kit.ts)。DEV の ?houndGrab=on で戻る。?altAttacks=houndGrab の直接指定は効く。
  houndGrab: {
    id: 'houndGrab', label: '掴み', kind: 'physical',
    chargeMs: 950, activeMs: 200, recoveryMs: 900,
    // 10-08 contact art: alpha>16 area=523035, striking tip source x=4;
    // bbox-centred, floor-anchored, scale=186*0.89/sqrt(area): paw reach ~174px.
    range: 174, strike: { localFromPx: 0, localToPx: 174 }, approachDistancePx: 159,
    parryable: false, unblockable: true, damage: PLAYER_DAMAGE.knightThrust, postureDamage: 45,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 130, crossingPolicy: 'track-until-commit',
    // Reuse the existing heavy (35 HP) tier; no pinned player state.
    preActiveLungeMs: 300, preActiveLungeDistance: 200, preActiveLungeStopGapPx: 100,
    contactAtActiveStart: true, sourceBossId: 'dream-hound',
  },
  houndRoar: {
    id: 'houndRoar', label: '灰の咆哮', kind: 'area',
    // 1300ms fails the existing wall-edge escape audit (196px needed, 184px budget).
    // 1450ms gives 198px while preserving the proposed 40..300px band.
    chargeMs: 1450, activeMs: 600, recoveryMs: 1000,
    parryable: false, range: 0,
    damage: PLAYER_DAMAGE.standard, postureDamage: 22, sourceBossId: 'dream-hound',
    hazardSpawns: [{ anchor: 'boss', offset: 170, width: 260,
      telegraphMs: 1450, activeMs: 600, damage: PLAYER_DAMAGE.standard,
      postureDamage: 22, hitOnce: true, kind: 'shock' }],
  },

  // ========== フィニッシャー (気絶中のプレイヤーに対する致命攻撃) ==========
  // 気絶 = 体幹崩しで動けない状態。ボスがその間に固有モーションで瀕死ダメ
  // parryable: false (気絶中で操作不能だが、念のため不可)、damage 3 (HP 5 の半分以上)

  // ハウンド フィニッシャー: 噛みついて首を振る
  houndExecution: {
    id: 'houndExecution',
    label: '噛みつき固め',
    kind: 'physical',
    chargeMs: 500,   // 短めの予兆 (気絶 1800ms 内で必ず確定)
    activeMs: 250,
    recoveryMs: 700,
    parryable: false,
    range: 140,
    strike: { localFromPx: 0, localToPx: 140 },
    approachDistancePx: 140,
    // 2026-07-05 王クローズ (実機・ユーザー): 死因の殆どが「自分で体幹崩し→致命で即死」だった。
    //   王=最初のボスは崩しを forgiving に = 致命を 60(命灯3)→25 へ王だけ下げる。満タンから崩しても
    //   HP75 残り「痛いが立て直せる」。共有 PLAYER_DAMAGE.finisher(60) は騎士/花嫁ら承認済みボス用に据え置き。
    damage: 25,
    bossFinisher: true,
    postureDamage: 30,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'boss-finisher',
    sourceBossId: 'dream-hound',
  },

  // ========== 待宵の花嫁 (bride) ==========
  // ヴェール一閃: 翼状ヴェールを左右に開いて横へ薙ぐ。斬撃 = parry。
  //   王 houndBite を基準にした baseline。lungeDistance は「床を滑る/ヴェールに引かれる」
  //   小さめの間合い詰め (range拡大やテレポートは不可・脚なしボス)。
  veilSweep: {
    id: 'veilSweep',
    label: 'ヴェール一閃',
    kind: 'physical',
    chargeMs: 700,
    activeMs: 150,
    recoveryMs: 560,
    parryable: true,
    range: 102,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。10-21 当たりの絵を veil_sweep_contact へ替え 138 → 102。旧 150 は ?bossReach=old。
    // 全開ヴェールは接触姿勢で左右へ実際に開くため、この技だけ背面側も明示する。
    strike: { localFromPx: -120, localToPx: 102 },
    approachDistancePx: 102,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 42,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'rear-coverage',
    lungeDistance: 120,
    sourceBossId: 'bride',
  },

  // 返しの一閃: ヴェール/赤/床を避けた直後に弾きへ切り替える短縮派生。
  //   geometry は veilSweep と同一。350ms の予告はワープ再出現470msとは別に必ず消化する。
  veilSweepLink: {
    id: 'veilSweepLink',
    label: '返しの一閃',
    kind: 'physical',
    chargeMs: 350,
    activeMs: 150,
    recoveryMs: 650,
    parryable: true,
    range: 102,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。10-21 当たりの絵を veil_sweep_contact へ替え 132 → 102。旧 150 は ?bossReach=old。
    strike: { localFromPx: -120, localToPx: 102 },
    approachDistancePx: 102,
    damage: 20,
    postureDamage: 42,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'rear-coverage',
    lungeDistance: 120,
    sourceBossId: 'bride',
  },

  // 喪の翻し: 同じヴェール語彙を赤い「弾くな」へ反転した技。
  //   もとは P3 三拍連 (弾き→赤→弾き) の中段だったが、その連は 2026-07-26 に撤去済み (bosses.ts の strings 内コメント)。
  //   今は通常戦闘の strings のどの連にも入っておらず、DEV の単技経路でだけ出る休眠資産。
  //   赤黒オーラ+danger SEは unblockable の共通経路を使い、花嫁専用例外を作らない。
  veilSweepPerilous: {
    id: 'veilSweepPerilous',
    label: '喪の翻し',
    kind: 'physical',
    chargeMs: 350,
    activeMs: 180,
    recoveryMs: 650,
    parryable: false,
    unblockable: true,
    range: 102,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。旧 150 は ?bossReach=old。
    strike: { localFromPx: -120, localToPx: 102 },
    approachDistancePx: 102,
    damage: 20,
    postureDamage: 34,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'rear-coverage',
    lungeDistance: 120,
    sourceBossId: 'bride',
  },

  // 三重の返礼: ワープ後だけ予約される3連弾き。初撃まで350ms、以後300ms間隔。
  //   3段とも veilSweep geometry を使い、各段active開始時に既存lunge目標を再計算する。
  veilBlinkTriple: {
    id: 'veilBlinkTriple',
    label: '三重の返礼',
    kind: 'physical',
    chargeMs: 350,
    activeMs: 150,
    recoveryMs: 850,
    comboHits: 3,
    comboGapsMs: [150, 150],
    comboGapMs: 150,
    parryable: true,
    range: 150,
    // 🔵 2026-10-05 当たりの前端 = 前の半円の糸 (エフェクト・src/bride_thread_sweep.ts THREAD_REACH_PX 135) + 15px。
    //   絵に合わせて 84 へ縮めた後、ユーザー「B エフェクトで射程を見せる」→ 見本から「B 前の半円の糸」で 150 へ戻した。旧の糸なし 150 は ?bossReach=old。
    strike: { localFromPx: -120, localToPx: 150 },
    approachDistancePx: 150,
    damage: 10,
    postureDamage: 18,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'rear-coverage',
    lungeDistance: 120,
    sourceBossId: 'bride',
  },

  // ① ヴェールの招待 — 位置よけの先生 (2026-07-10 機構再設計)。
  //   設計正本: docs/candidates/2026-07-10-bride-evasion-rescope-flurry.md §4「🎯 機構の再設計」。
  //   判定の本体は凍結ゾーン (range:0 = melee ヒットは湧かない)。 両ゾーンとも anchor='player' =
  //   花嫁からの距離に依らず「stand=被弾／前回避1回=セーフ／前回避2回=被弾」を確実にする。
  //   tb 図 (tb = 花嫁側へ + の座標 = -offset。 足元 tb=0 / 前回避1回 tb≈+stepDistance(90) / 2回 tb≈+180):
  //     ① 足元＋後方バンド tb≈[-100,+10]: 足元(居座り)と短い後退を潰す。 前端は足元のちょい先で止め、
  //        1回踏み込めば抜ける。 後方は ~100px で止める = 壁際で前(懐)が塞がっても後退で逃げ切れる
  //        公平性 (hazard_escape) を満たす範囲 (⬜暫定・当初 300px 案は壁詰めで逃げ場ゼロ→縮小)。
  //     ② オーバーシュートバンド tb≈[+130,+400]: 懐の隙間の前 = 前回避2回目の着地点 (マッシャー狩り)。
  //        近距離で出るので視覚は「花嫁の後ろ側」に重なる。
  //     セーフ = ①②の隙間 = 狭い懐バンド tb(+10,+130)。 前回避1回(+90)がちょうど収まる。
  //   ⬜ offset/width は全て暫定 = 実機＋テスト (tests/bride_veil_invitation.test.ts) で調整。
  //   parryable:false。 技の kind は 'physical' (見た目の分類・上の定義)。 'veil' は下の hazardSpawns の各ゾーン (HazardKind) の kind で、
  //   「床のヴェール」(刃で受けない・純位置よけ) を指す。 技そのものの kind ではない (2026-07-26 友達試遊の整理 8-7-4・2026-10-13 コメント修正)。
  veilInvitation: {
    id: 'veilInvitation',
    label: 'ヴェールの招待',
    kind: 'physical',
    chargeMs: 850,
    activeMs: 600,
    recoveryMs: 700,
    parryable: false,
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 30,
    sourceBossId: 'bride',
    hazardSpawns: [
      // ① 足元＋後方バンド: tb≈[-100,+10] (offset 45/width 110 → center tb=-45)。
      //   足元(tb=0)を覆い短い後退(負tb)を潰す。 前端(tb=+10)は足元のちょい先 = 前回避1回(+90)で抜ける。
      //   後方は ~100px で止める = 壁際で前(懐)が塞がっても後退で逃げ切れる公平性 (hazard_escape green)。 ⬜暫定
      { anchor: 'player', offset: 45, width: 110, telegraphMs: 850, activeMs: 600,
        damage: PLAYER_DAMAGE.standard, postureDamage: 30, hitOnce: true, kind: 'veil' },
      // ② オーバーシュートバンド: tb≈[+130,+400] (offset -265/width 270 → center tb=+265)。
      //   懐の隙間の前 = 前回避2回目(+180)の着地点 (連打=マッシャー狩り)。 旧 boss アンカー背面ヴェールの置換。 ⬜暫定
      { anchor: 'player', offset: -265, width: 270, telegraphMs: 850, activeMs: 600,
        damage: PLAYER_DAMAGE.standard, postureDamage: 30, hitOnce: true, kind: 'veil' },
    ],
  },

  // ② 誓いの抱擁 — 掴み = 短い1ヒット + リーチ大 (hit-timeline §5・2026-06-11 再導出)。
  //   「両腕で抱きにかかる」一瞬の動き。 避けられたら空振り = 丸腰 → 反撃が通る (emergent)。
  //   リスク = リーチ170 (veilSweep より広い・離れて立つだけでは外しにくい)
  //   リターン = 回避1回 (i-frame 200 > 窓180) で確実に凌げて、 直後に反撃が決まる。
  //   旧・凍結ゾーン版 (近づくと危険の帯・カメ対策の反転) は退役: カメ対策の役は
  //   「位置でなくタイミングを要求する」この形が引き継ぐ (tests/no_camp_spot.test.ts の新不変条件)。
  //   致命 brideExecution はこの完成版。
  vowEmbrace: {
    id: 'vowEmbrace',
    label: '誓いの抱擁',
    kind: 'physical',
    chargeMs: 850,
    activeMs: 180,
    recoveryMs: 760,
    parryable: false,
    unblockable: true,   // 裁定C: 抱擁=掴み=弾けない=赤 (花嫁=回避の教師)
    range: 79,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。旧 170 は ?bossReach=old。
    // 背面カバー (2026-07-22 ユーザー裁定A・shadeThrust と同趣旨)。
    //   検査 = tests/combat_sim/dodge_counter_audit.test.ts
    strike: { localFromPx: -60, localToPx: 79 },
    approachDistancePx: 79,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 34,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 180,
    crossingPolicy: 'track-until-commit',
    lungeDistance: 100,
    recoveryDriftPx: 45,   // 掴み: recovery (embrace_collapse) で前へ手を伸ばす前進。 ドラフト・H-1調整
    sourceBossId: 'bride',
  },

  // ③ 破れたバージンロード — 位置取り (空間ハザードの本丸)。
  //   床4セグメント+安全な隙間 (100px×3)。 隙間に「立つ」のが唯一の答え。
  //   持続 700ms ≫ i-frame 130ms = タイミングでは抜けられない。
  //   2026-06-11 プレイテスト修正: 旧3セグメント(隙間2)は左壁際から最寄りの隙間まで285px
  //   = どう操作しても被弾確定だった。 隙間を3つに増やし最遠でも125pxに (移動上限155px内)。
  thornAisle: {
    id: 'thornAisle',
    label: '破れたバージンロード',
    kind: 'area',
    chargeMs: 1000,
    activeMs: 700,
    recoveryMs: 800,
    parryable: false,
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 26,
    sourceBossId: 'bride',
    hazardSpawns: [
      // ARENA: leftWall 60 / rightWall 900。 隙間 = [170..270] / [430..530] / [690..790]
      { anchor: 'arena', offset: 115, width: 110, telegraphMs: 1000, activeMs: 700,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'thorns' },
      { anchor: 'arena', offset: 350, width: 160, telegraphMs: 1000, activeMs: 700,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'thorns' },
      { anchor: 'arena', offset: 610, width: 160, telegraphMs: 1000, activeMs: 700,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'thorns' },
      { anchor: 'arena', offset: 845, width: 110, telegraphMs: 1000, activeMs: 700,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'thorns' },
    ],
  },

  // ⓪ 裾乱れ (裏取り連撃) — 花嫁の看板技 (2026-07-10 導入)。
  //   設計正本: docs/candidates/2026-07-10-bride-evasion-rescope-flurry.md §3 (リズム/機構/向きロック確定)。
  //   リズム (rhythmtap v2・250ms版): 溜め1600 → たん(初撃=向きロック) → 250刻み高速連×6 →
  //     フェイント(gap600・ノーダメの釣り) → だん(gap800・本命)。
  //   機構: 長い溜めでは追うが、active直前に向きを固定。hit0後とhit6後だけ gap で向き直る。
  //     毎hit追尾はせず、早い一度の背後抜けだけで全段を無力化できないようにする。
  //   comboStages: index7=フェイント (damage:0・釣りの空振り) / index8=だん (強打・本命、上乗せ)。
  //   ⚠️ lungeDistance 未指定 (=踏み込みなし・その場で旋回する据え置き型) — 「背後に抜けたら
  //     連打が当たらない」要件を computeLungeTargetX の再追尾 (comboGap 毎回 player.x 参照) から
  //     完全に切り離すための意図的選択 (指示から変えた点・完了報告参照)。
  //   ⚠️ 数値 (damage/postureDamage/activeMs/recoveryMs/range) は全て⬜暫定・数値調整フェーズ送り。
  veilFlurry: {
    id: 'veilFlurry',
    label: '裾乱れ',
    kind: 'physical',
    chargeMs: 1600,           // 長い溜め (見逃せない大予兆)
    activeMs: 160,            // 各ヒット ⬜暫定
    recoveryMs: 1000,         // だん後の大隙=攻め時 ⬜暫定
    comboHits: 9,             // hit0=たん(初撃) / hit1-6=たたたたた(高速連) / hit7=フェイント / hit8=だん
    // ⚠️ 実ヒット間隔 = activeMs(160) + comboGap。 250ms間隔で発生させたい高速連は gap=90 (160+90=250)。
    //   2026-07-10 ユーザー要望「250ms感覚で攻撃が発生」。 旧 [400,250..] は gap を間隔と誤設定=実 410ms/発で鈍かった。
    //   [0]=たん→初手 400ms (向きロック後の間+背後回避の窓を残す) / [1-5]=たたたた 250ms / [6]=フェイント前600ms / [7]=だん前800ms。
    //   ※ 250ms < パリィ1サイクル(320ms) = 高速連は全段パリィ不可へ寄る(=背後回避が主答え)。 実機で要確認。
    comboGapsMs: [240, 90, 90, 90, 90, 90, 440, 640],
    comboGapMs: 90,           // フォールバック
    parryable: true,          // 全段パリィ可能 (§3 ユーザー裁定)
    range: 150,               // 近〜中・facing 前方のみ届く (背後に抜けたら当たらない設計)
    // 🔵 2026-10-05 当たりの前端 = 前の半円の糸 (エフェクト・src/bride_thread_sweep.ts THREAD_REACH_PX 135) + 15px。
    //   絵に合わせて 84 へ縮めた後、ユーザー「B エフェクトで射程を見せる」→ 見本から「B 前の半円の糸」で 150 へ戻した。旧の糸なし 150 は ?bossReach=old。
    strike: { localFromPx: 20, localToPx: 150 },
    approachDistancePx: 150,
    damage: 8,                // 各ヒット (hit0-6の基準値) ⬜暫定
    postureDamage: 14,        // 各ヒット ⬜暫定
    turnsDuringTelegraph: true,   // 溜め中は追う→初撃で facing 固定 (裏取り成立の絶対条件)
    facingLockBeforeActiveMs: 160,
    crossingPolicy: 'track-until-commit',
    comboStages: [
      { strike: { localFromPx: 20, localToPx: 150 } },   // hit0 = たん (初撃)
      { strike: { localFromPx: 20, localToPx: 150 } },
      { strike: { localFromPx: 20, localToPx: 150 } },
      { strike: { localFromPx: 20, localToPx: 150 } },
      { strike: { localFromPx: 20, localToPx: 150 } },
      { strike: { localFromPx: 20, localToPx: 150 } },
      { strike: { localFromPx: 20, localToPx: 150 } },
      // hit7 = だんへの「振りかぶり」= 予備動作の1拍 (2026-07-11 ユーザー裁定)。 range:0 = 当たり判定なし
      //   (spawnMeleeHit が range≤0 で null → 帯を出さない)。 弾く対象が無い純・予備動作にして、
      //   「振りかぶりの動きを見て早めに守り/回避を切った人が、640ms後のだんに間に合わず被弾」という
      //   釣りを成立させる。 damage/posture も 0。 絵は振りかぶり (発注中・暫定 flurry_feint)。
      { damage: 0, postureDamage: 0, range: 0 },
      // hit8 = だん (強打・本命・唯一の重い一撃) ⬜暫定。 2026-07-10 ユーザー裁定「knockback外して重いダメージ+揺れ」:
      //   旧 knockbackPx:40 は hits.ts の knockback 分岐が最優先で hit を consume し damage/posture/parry/揺れを
      //   全部スキップ = 本命が無害化するバグだった。 撤去して重い一撃へ (揺れは game.ts の着地シェイクで別途)。
      // 真下へ落とす一撃。背面への許容は短く、90pxの背後スリップは安全。
      { damage: 24, postureDamage: 34, strike: { localFromPx: -45, localToPx: 109 } },
    ],
    // 移動プロファイル (2026-07-10 ユーザー案・連続+ease化): hit0=追尾で間合いを作り、 hit1-6=コミット方向へ
    //   連続前進(旋回しながら詰める)、 hit7(フェイント)/hit8(だん)=停止して足を止め真下へ叩きつけ。 背後スリップは
    //   前進で離れ逃げ窓維持、 下がり逃げは前進で捕まる。 comboAdvanceX が毎フレーム連続適用でカクつき無し。 数値⬜暫定。
    comboLunge: { followHits: 1, followSpeed: 750, advanceSpeed: 180, plantFromHit: 7 },
    sourceBossId: 'bride',
  },

  // 花嫁の致命 (崩し気絶への処刑) = 誓いの抱擁の完成版。
  //   「花嫁がずっと完成させたかった抱擁が、崩した時にだけ成就する」(spec §4)。
  //   王 houndExecution と同役: parryable:false / bossFinisher:true。
  brideExecution: {
    id: 'brideExecution',
    label: '誓いの抱擁・完',
    kind: 'physical',
    chargeMs: 550,
    activeMs: 300,
    recoveryMs: 900,
    parryable: false,
    range: 105,
    // 🔵 2026-10-05 当たりの前端 = 描かれた絵の前端 + 15px (ユーザー「A 絵に合わせる」・src/bride_shade_reach.ts)。旧 150 は ?bossReach=old。
    strike: { localFromPx: 20, localToPx: 105 },
    approachDistancePx: 105,
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'boss-finisher',
    lungeDistance: 120,
    recoveryDriftPx: 55,   // 致命も同モーション流用 = 前へ踏み込む抱擁。 ドラフト・H-1調整
    sourceBossId: 'bride',
  },

  // ========== 主なき近衛騎士 (vigil-knight) ==========
  // ユーザー設計 (2026-06-11 たたき台 txt): 主題は「間合い」と「攻め時」。
  //   - 槍 = 境界線。 突く/払う/床に立てる、 すべて「ここから先は通さない」の意味で統一。
  //   - 激しく追わない。 ゆっくり間合いを詰める「近づいてくる境界線」(walkSpeedMul 0.55)。
  //   - 攻撃時だけ急に速い。 静けさと突きの鋭さの差で怖さを出す。
  //   - 答えは攻撃後の隙 (長い recovery) にだけ踏み込むこと。

  // 槍の間合いの主役。 長い溜め → 鋭い突き → 「長い隙」= このボス唯一の大きな攻め時。
  //   evade-counter: 見切って前へ抜ければ反撃窓。 下がるだけだと槍が伸びて届く (lunge 240)。
  lanceThrust: {
    id: 'lanceThrust',
    label: '宣告の突き',
    kind: 'physical',
    chargeMs: 800,
    activeMs: 200,
    recoveryMs: 1150,   // ★騎士の核: 突きの後の長い静止 = 「ここで踏み込め」の教育
    parryable: false,   // 答え: 見切り (回避で窓を跨ぐ→反撃)
    unblockable: true,  // 裁定C: 突き=弾けない=赤
    // 🔵 2026-09-27 届き = 描かれた穂先 (lanceThrust_full の実測 128.2px・src/knight_reach.ts)。旧 250 は絵の約2倍まで当たっていた
    //   (ユーザー原文「武器が明らかにあたっていないのに当たり判定がある（複数人から指摘を受けている）」)。旧値は DEV ?knightReach=old。
    // 🔵 2026-09-30 128 → 138 (ユーザー「突きはAで伸ばして」= 薙ぎと同じく当たりを絵に合わせる)。兜で揃える大きさの既定化で
    //   lanceThrust_full の穂先が 137.9px (最初のコマ) / 133.2px (以降) に伸びた。踏み込みの着地 (approachDistancePx 128) は変えない。
    range: 138,
    // 背面カバー (2026-07-22 ユーザー裁定A): 判定が前方だけだと、 回避で懐へ入った/抜けた瞬間に
    //   判定の外へ出て **タイミングが合っていても見切りが開かない** (hits.ts は overlapsPlayer の
    //   内側でしか counterWindow を開かない)。 実測 = 立ち位置x時刻x向き 180通り中 33〜51%しか
    //   見切りが取れていなかった。 後ろ側へ伸ばして「抜けても判定の中」にし、 見切りを
    //   *位置ゲー* から *タイミングゲー* へ戻す。 外側の到達距離 (localToPx) は不変 = 間合いは変えない。
    //   ※ 背面カバー自体は既存語彙 (veilSweep -120 / houndLunge -45 等)。 新機構ではない。
    //   検査 = tests/combat_sim/dodge_counter_audit.test.ts
    strike: { localFromPx: -60, localToPx: 138 },
    // 踏み込みの着地 = 主人公の手前 128-28=100px (穂先が体に届く位置)。旧 250 (着地 222)。
    approachDistancePx: 128,
    damage: PLAYER_DAMAGE.knightThrust,
    postureDamage: 40,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 180,
    crossingPolicy: 'track-until-commit',
    lungeDistance: 240,
    sourceBossId: 'vigil-knight',
  },

  // 近づきすぎた者への「払い」。 騎士の至近をうろつく相手を儀礼的に払う = parry の出題。
  lanceSweep: {
    id: 'lanceSweep',
    label: '境界の薙ぎ',
    kind: 'physical',
    chargeMs: 700,
    activeMs: 150,
    recoveryMs: 640,
    parryable: true,    // 答え: 弾き
    // 2026-09-30「当たりを絵に合わせて伸ばす」: helmet-fit spear tip 109.9px, hit 110px.
    //   旧 200 は絵の約2倍まで当たっていた。旧値は DEV ?knightReach=old (src/knight_reach.ts)。
    range: 110,
    strike: { localFromPx: -45, localToPx: 110 },
    // 2026-09-30「当たりを絵に合わせて伸ばす」: hit 110; director keeps approach 95 / lunge 90 (landing unchanged).
    // 踏み込みの着地 = 主人公の手前 95-28=67px。旧 200 (着地 172)。
    approachDistancePx: 95,
    damage: PLAYER_DAMAGE.knightSweep,
    postureDamage: 45,
    lungeDistance: 90,
    turnsDuringTelegraph: true,   // 払いは追う (§4.3): 裏へ歩いただけでは外せない
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'rear-coverage',
    sourceBossId: 'vigil-knight',
  },

  // 石突の返し (K-2 密着咎め・幕2解禁): 至近で張り付く相手を、 石突の返しで前帯ごと払う。
  //   帯 = [騎士+120..騎士+220] ≒ 「殴りに行く立ち位置」そのもの。 答えは外へ離れるか裏へ抜ける。
  //   🔴 2026-09-06 死んだ前提 (docs/candidates/2026-09-06-boss-core-real-size-fable-design.md §8-4):
  //   ~~懐 (<120) は衝突 (minBodyDistance 108) でほぼ立てない~~ は誤り。 主人公に効いているのは
  //   「芯」(BOSS_CORE・既定28+15=43px) であって、 minBodyDistance 108 は花嫁の comboAdvanceX にしか
  //   使われていない (主人公は実際には 43px まで歩ける)。 石突の帯そのものは正しい (懐が安全は設計どおり・06-11)。
  //   幅100 = 壁際に押し込まれていても帯を横断できる (hazard_escape.test.ts が自動担保)。
  //   ゾーン持続 260ms > i-frame 200ms = 位置で離れるのが答え (roster_integrity 不変条件)。
  lanceButt: {
    id: 'lanceButt',
    label: '石突の返し',
    kind: 'area',
    chargeMs: 700,
    activeMs: 260,
    recoveryMs: 750,
    parryable: false,
    range: 0,
    damage: PLAYER_DAMAGE.knightButt,
    postureDamage: 35,
    preferClose: true,
    sourceBossId: 'vigil-knight',
    hazardSpawns: [{
      anchor: 'boss', offset: 170, width: 100, telegraphMs: 700, activeMs: 260,
      damage: PLAYER_DAMAGE.knightButt, postureDamage: 35, hitOnce: true, kind: 'buttTrap',
    }],
  },

  // 切り下がり (その場で払って跳び下がる・10-01 夜まで詰めてから)。移動は boss.ts が担当する (src/knight_leap_slash.ts knightCrossForwardPx)。
  lanceCross: {
    id: 'lanceCross',
    label: '境界の薙ぎ下がり',
    kind: 'physical',
    passThroughBody: true,
    chargeMs: 900,
    activeMs: 220,
    recoveryMs: 1250,
    parryable: true,     // 弾ける横薙ぎ
    // 🔵 2026-09-30 185 → 200「当たりを絵に合わせて伸ばす」 = 薙ぎの剣先 (穂先 110 + 踏み込み 90) と同じ間合い (ユーザー「薙ぎ攻撃の時の剣先当たりが
    //   この武器の間合いだからそれより近いときに」)。踏み込まないぶん、穂先の先 (穂先〜200) は弧の外側の剣圧の三日月
    //   (render.ts drawKnightLeapSlash・ユーザー選択「範囲を広げてエフェクトで」「弧の外側に剣圧の三日月」) で見せる。
    // 🔵 2026-10-01 詰めて→払って→跳び下がる (ユーザー「一回前に詰めてから薙ぎ払って下がるようにするとかすれば」)。
    //   払いは薙ぎと同じ絵・同じ当たり (穂先 110px) で、最大 90px 踏み込む (src/knight_leap_slash.ts knightCrossForwardPx)
    //   = 届く所は 09-30 と同じ 200px だが、穂先が本当にそこまで行く。当たりは騎士と一緒に動き (跳び始めに残さない)、
    //   active 220ms (踏み込み 150 + 振り切り 70) で消える = 跳び下がりの間は当たらない。
    //   経緯: 09-30 200px (跳びながら払い、穂先の先は光で見せた) → 10-01 105px (光が「前に光線」) →「攻撃も当たらない」→ この形。
    // 🔵 2026-10-01 夜 前に出ない (ユーザー「前に出てから後ろに飛ぶのやっぱりおかしいよな。当たらなかったとしてもいいから前に出ずに
    //   切り下がり薙ぎとして完成させてほしい」): 踏み込み 0・その場で払って 50ms で跳び下がる (src/knight_leap_slash.ts)。
    //   当たりは穂先 110px まで = range 200 の端にいる主人公には当たらない (了承済み)。
    // 🔵 2026-10-02 range・approachDistancePx 200 → 110 (ユーザー「デフォルトで発動する距離感が悪いか、攻撃範囲が悪い」
    //   「距離を調整するならこの時も当たり判定と武器やエフェクトがあたっているように見えるように」→ 選択「近い時の技に戻す」):
    //   この技は寄らずその場で払う (boss.ts computeLungeTargetX)。いつもの間合い (170〜190px) から出て穂先が一度も届かなかった。
    //   出すのは穂先が主人公の体の中心まで届く所 (110px) の内側だけ (src/knight_spacing.ts KNIGHT_CROSS_MIX_RANGE_PX)。
    //   当たりは光の先と一緒に伸びる (hits.ts meleeStrikeNow・src/knight_cross_arc.ts knightCrossStrikeFrontPx)・跳ぶのは払い切ってから。
    range: 110,
    strike: { localFromPx: -45, localToPx: 110 },
    approachDistancePx: 110,
    damage: PLAYER_DAMAGE.knightCross,
    postureDamage: 40,   // 弾き技 (lanceSweep 45 より軽め)
    lungeDistance: 0,
    positionEvadable: true,
    crossingPolicy: 'intentional-opening',
    crossArena: true,
    sourceBossId: 'vigil-knight',
  },
  // 突き飛ばし (2026-10-01 ユーザー「介錯の武器変形の時みたいにダメージのない攻撃で突き飛ばして距離を離すのはどう？」
  //   →「弾ける・弾けば押されない」「既存の横構えの絵で試す」)。近い時の単発の薙ぎの代わりに出る (src/knight_spacing.ts KnightCloseAnswer)。
  //   槍を縦に立てて構え (専用の絵 lanceShove_windup・10-01 夜「押す絵を作った方がいい」・最初の試作は lanceCross_backPlant) 踏み込み、柄で主人公を 140px 押し出す。痛くない。弾けば押されず、通常のパリィの報酬が付く。
  //   当たり = 柄の前端 70px まで。踏み込みは薙ぎと同じ最大 90px で、主人公の 50px 手前で止まる (knightSpearLungeGap)。
  lanceShove: {
    id: 'lanceShove',
    label: '柄の押し返し',
    kind: 'physical',
    chargeMs: 180,   // 10-01 夜 700 → 180 = 人がぎりぎり反応しきれない極小の溜め (ユーザー「予備動作無しで唐突に出してOK」「極小溜め（人間がギリギリ反応しきれない）→押し返しにしてほしい」)
    activeMs: 300,   // 10-01 夜 180 → 300: 踏み込みを 240ms のなめらかな一歩に (下の forwardWarp)
    // 10-01 夜 ユーザー「押し返しがなんだか早送りしたみたいになってる」→「踏み込みが速すぎる」: 共通の踏み込み
    //   (lungeSpeed 1900px/s の等速) だと 140px を約 0.07 秒で滑っていた。溜め 180ms はそのまま、踏み込みだけ
    //   240ms かけて加速→減速 (smoothstep・最高 約 875px/s) で寄せ、着いた所で押す。残り 60ms は押し切りの静止。
    forwardWarp: [0, 0.043, 0.156, 0.316, 0.5, 0.684, 0.844, 0.957, 1, 1, 1],
    recoveryMs: 700,
    parryable: true,
    range: 200,
    strike: { localFromPx: -20, localToPx: 70 },
    approachDistancePx: 95,
    damage: 0,
    postureDamage: 30,   // 押された時は使わない (押し出しが先)。弾いた時の火花の強さだけに効く
    knockbackPx: KNIGHT_SHOVE_PUSH_PX,
    knockbackParryable: true,
    lungeDistance: 90,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'rear-coverage',
    sourceBossId: 'vigil-knight',
  },
  knightWallCharge: {
    id: 'knightWallCharge', label: '壁ぎわの突進', kind: 'physical',
    chargeMs: 700, activeMs: 1000, recoveryMs: 1550,
    parryable: false, unblockable: true, passThroughBody: true,
    // 2026-09-30 当たりは突きと同じ (128 → 138・tests/knight_wall_charge.test.ts が突きとの一致を縛る)。止まる位置の approach は据え置き。
    range: 138, strike: { localFromPx: -60, localToPx: 138 },
    approachDistancePx: 128, crossingPolicy: 'intentional-opening',
    damage: PLAYER_DAMAGE.knightThrust, postureDamage: 40,
    // 2026-10-08 ユーザー採用 (C-1「こちらが良い」): 走り終わりまで当たる (覚醒王の走る赤と同じ)。旧 = 走り出しの約0.1秒だけ。DEV ?knightChargeHit=old で旧へ (knight_charge_hit.ts)。
    keepHitWhileTravelling: true,
    sourceBossId: 'vigil-knight',
  },

  // 騎士の致命 (崩し気絶への処刑): 境界線そのものである槍で、 静かに刺し貫く。
  knightExecution: {
    id: 'knightExecution',
    label: '境界の宣告・終',
    kind: 'physical',
    chargeMs: 550,
    activeMs: 250,
    recoveryMs: 850,
    parryable: false,   // 崩し気絶への処刑 (読みでなく見せ場)
    range: 250,
    strike: { localFromPx: 45, localToPx: 250 },
    approachDistancePx: 250,
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'boss-finisher',
    lungeDistance: 150,
    sourceBossId: 'vigil-knight',
  },
  // 処刑人 G11「執行の咆哮 → 突進斬り」: フェーズ移行技 (2026-07-03)。 HP 60% で 1 回だけ発動。
  //   ⚠️ 設計: 咆哮 3 波 (白い衝撃波・boss中心同心波) → 500ms 「トラップ間」 → 突進斬り。
  //     3 波で防御慣らして、 反射組が防御解いた瞬間に突進斬り = 反射依存を裏切る (SEKIRO Genichiro 系)。
  //   タイムライン: 0-2300ms telegraph (咆哮 FX + 3 波 hazard) → 2300-2500 active (突進斬り) → 700ms recovery。
  //     波 1: telegraph=1200ms 発火・[boss±60〜±120] ext 帯・damage=10 (軽)
  //     波 2: telegraph=1500ms 発火・[boss±120〜±180]・damage=10
  //     波 3: telegraph=1800ms 発火・[boss±180〜±240]・damage=15
  //     突進斬り: boss active t=2300ms・lungeDistance=300 で前方へ突進しつつ横薙ぎ・damage=30
  //   全ヒット parryable。 咆哮 3 波 + 突進斬り = 合計 65HP (JSON 準拠)。
  //   ⚠️ 咆哮 FX は render.ts で telegraph 中の白 5 レイヤーを描画: 吸気→peak→歪み波→光条→シルエット閃光。
  //   設計正本: docs/candidates/executioner-attacks/G11-phaseTransition.json
  //   ⚠️「参考のみ・完全模倣禁止」: ER Hoarah Loux (Godfrey ph2) / DS3 Nameless King ph1 / SEKIRO Demon of Hatred。
  executioner_phaseTransition: {
    id: 'executioner_phaseTransition',
    label: '執行の咆哮',
    kind: 'physical',
    chargeMs: 2300,         // 咆哮 + 3 波 hazard + トラップ間 (500ms 波3 後)
    activeMs: 200,          // 突進斬りのヒット窓
    recoveryMs: 700,
    parryable: true,
    range: 150,             // 突進斬りの melee range
    strike: { localFromPx: -80, localToPx: 155 },
    approachDistancePx: 150,
    damage: 30,             // 突進斬り単体 (咆哮 3 波は hazard 側 damage で別途 10+10+15=35)
    postureDamage: 30,
    lungeDistance: 300,     // 大きな突進 (JSON 準拠 = 300)
    turnsDuringTelegraph: true,   // 咆哮中もプレイヤーの位置を追う (横抜け防止)
    facingLockBeforeActiveMs: 220,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
    hazardSpawns: [
      // 波 1 (内側・最初) — [boss±60〜±120], 発火 t=1200
      { anchor: 'boss', offset: 90, width: 60, telegraphMs: 1200, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      { anchor: 'boss', offset: -90, width: 60, telegraphMs: 1200, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      // 波 2 — [boss±120〜±180], 発火 t=1500
      { anchor: 'boss', offset: 150, width: 60, telegraphMs: 1500, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      { anchor: 'boss', offset: -150, width: 60, telegraphMs: 1500, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      // 波 3 (外側・最後) — [boss±180〜±240], 発火 t=1800
      { anchor: 'boss', offset: 210, width: 60, telegraphMs: 1800, activeMs: 250,
        damage: 15, postureDamage: 20, hitOnce: true, kind: 'roarWave' },
      { anchor: 'boss', offset: -210, width: 60, telegraphMs: 1800, activeMs: 250,
        damage: 15, postureDamage: 20, hitOnce: true, kind: 'roarWave' },
    ],
  },
  // 処刑人の致命「執行」: 刃の一撃で咎人を斬り伏せる。打点の絵は exec_contact_v2 (sprites.ts SPRITE_FILE_OVERRIDE・10-09 採用)。
  executioner_finisher: {
    id: 'executioner_finisher',
    label: '執行',
    kind: 'physical',
    chargeMs: 600,
    activeMs: 250,
    recoveryMs: 900,
    parryable: false,      // 崩し気絶への処刑 (見せ場)
    range: 170,
    strike: { localFromPx: 20, localToPx: 170 },
    approachDistancePx: 170,
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'boss-finisher',
    lungeDistance: 120,
    sourceBossId: 'the-executioner',
  },

  // ========== 請われぬ介錯 — 刃5本＋砲3本 (2026-08-12) ==========
  // 正本 = docs/decision-ledger.md §請われぬ介錯 2026-08-12。
  // 機構 = src/executioner_weapon.ts (幕1は刃、幕2から砲との二形態変形)。
  //
  // ■ 構成の芯 —「3つの骨格 × 3つの武器」= 9本。技数は減らし、分岐を増やす (台帳の方針)。
  //   骨格は**体**が作る。武器が変えるのは**軌道と正解防御**だけ。
  //     骨格A 振り上げて落とす (Drop)  … 両手を頭上へ
  //     骨格B 腰を落として溜める (Ground) … 低く沈む
  //     骨格C 踏み込んで伸ばす  (Reach) … 前足を踏み出す
  //   → 武器が変わっても「足元を見る癖」が資産として残る (ER ホーラ・ルーが斧を捨てても
  //      踏みつけ→衝撃波の構造が残るのと同じ。2026-07-29 参考調査)。
  //
  // ■ 🔴 このボスの一貫ルール —「**地面を這って来る打撃は弾けない (赤)**」
  //   exec_maulQuake (地割り) と exec_wheelRoll (転がし) だけが赤。ゴッドフリーの
  //   「地面を経由する技=パリィ不可」と同じ一貫性を、覚えるのでなく**法則で**読ませる。
  //   ⚠️ 掴み (exec_yokeGrab) が赤なのは全ボス共通の別ルール (裁定C)。
  //   ⚠️ 床ハザード (ゾーン) は赤を付けない = 既存の紫予兆ラインが役割を持つ (台帳)。
  //
  // ■ damage / postureDamage は「枷 (yoke) を等倍」とした基準値。
  //   実際に出る値は武器倍率 (EXECUTIONER.weapons[*].damageMul / postureMul) が掛かる。
  //   → つまり **同じ技が、持っている道具で痛さまで化ける**。

  // --- 骨格A: 振り上げて落とす ---------------------------------------------
  // 刃。重い・遅い・痛い。recovery が全技で最も深い = 「重量=硬直=報酬」の等価交換を
  //   絵で見せる (星砕きのラダーンが剣を地面から抜くまでを確定反撃窓にしているのと同じ)。
  exec_maulDrop: {
    id: 'exec_maulDrop',
    label: '振り下ろし',
    kind: 'physical',
    chargeMs: 850,
    activeMs: 190,
    recoveryMs: 1200,       // ★このボス最長級の隙。弾いても避けても「殴っていい」が明確に返る
    parryable: true,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 range 195 / strike{15,195} / approachDistancePx 150 / lungeDistance 40）
    range: 156,
    strike: { localFromPx: 12, localToPx: 156 },
    approachDistancePx: 120,
    damage: 24,
    postureDamage: 42,
    lungeDistance: 32,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 150,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
  },
  // 待ち落とし。🔴 旧「固定ディレイ +450ms／2026-08-04票を正本」は却下済み。
  // 現行は据える→半歩引く→振り上げる動作で発射時刻を伝える。
  // 正本: docs/candidates/2026-08-06-executioner-aim-charge-redesign.md
  // 🔴 activeMs と接触位置 (active 0.35) は振り下ろしと同一に保つ。動かすと「絵と判定が壊れて見える」側になる。
  exec_maulHold: {
    id: 'exec_maulHold',
    // 🔵 2026-08-28 呼び名 ~~待ち落とし~~ → 斬り上げ。動きが 08-26 に「構え→低く引く→掬い上げ」へ
    //   変わり、名前だけ落とす技のまま残っていた (「絵と同時に変える」の宿題を絵の差し替えと同時に消化)。
    label: '斬り上げ',
    kind: 'physical',
    chargeMs: 1650,
    activeMs: 190,
    recoveryMs: 1350,
    parryable: true,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 range 195 / strike{15,195} / approachDistancePx 150 / lungeDistance 40）
    range: 156,
    strike: { localFromPx: 12, localToPx: 156 },
    approachDistancePx: 120,
    damage: 28,
    postureDamage: 46,
    lungeDistance: 32,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 150,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
  },

  // --- 骨格B: 腰を落として溜める -------------------------------------------
  // 刃。地面へ叩き込み、割れ目が前後へ走る。★一貫ルール = 地面を這うので弾けない。
  exec_maulQuake: {
    id: 'exec_maulQuake',
    label: '地割り',
    kind: 'area',
    chargeMs: 1500,
    activeMs: 190,
    recoveryMs: 1250,
    parryable: false,
    unblockable: true,      // 裁定C: 赤黒い呪いオーラ + danger SE
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 range 150 / strike{-40,150} / approachDistancePx 150 / lungeDistance 20）
    // 🔵 2026-09-03 第3便: range 120→100・strike localToPx 120→100・lungeDistance 16→0（突き立てで前へ出ない）
    range: 100,
    strike: { localFromPx: -32, localToPx: 100 },
    approachDistancePx: 120,
    damage: 24,
    postureDamage: 36,
    lungeDistance: 0,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 200,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
    // 🔵 2026-09-03 第4便 ユーザー「砲形態のように予備動作で距離を取ってから発動しないと、
    //   床技が脅威でない」: 溜めの頭 220ms で 150px 飛び退いてから突き立てる。
    //   （200 は床攻撃の公平性テスト tests/hazard_escape.test.ts で壁際 236px > 222px になった。150 が通る最大）
    telegraphBackstepPx: 150,
    telegraphBackstepMs: 220,
    // 🔵 2026-10-02 ユーザー裁定 (AI点検の候補2「地割り1回で2発」): 1回の地割りからは1発だけ。
    //   叩きつけ 24＋床 14 (0.1秒差)・床 14＋14 (0.2秒差) が両方入り、崩れ→執行へ繋がっていた。
    //   本体の一撃も同じ束に入れる (game.ts latestCastGroup)。戻す＝この行を消す。
    hazardHitOncePerCast: true,
    // 割れ目 = 床ハザード。「位置で避ける」を伝える。
    //   遅れて爆ぜる = 「振り終わり=安全」の油断を潰す (ゴッドフリーの地割れ)。
    // 🔴 廃止 (2026-07-31): ~~赤ではなく既存の紫予兆ラインで~~。台帳の「地割り(**赤**・床ハザード付き)」
    //   および上の unblockable=裁定C と矛盾していた。実機では暗い床に暗い線で見えず、
    //   割れ目の底へ CURSE_* の赤い加算光を入れた。描画 = src/render.ts の hz.kind === 'quake'。
    //   出所 = docs/decision-ledger.md「常打ちの刑吏 / 骨格B」+ docs/candidates/2026-07-31-executioner-quake-telegraph-ticket.md
    // 🔴 廃止 (2026-09-03 第3便): ~~offset ±68/±128・width56・telegraphMs 1750/2250 の4本~~
    //   （🔵 2026-09-02 夜 等身1.6化で0.8倍・旧 offset ±85/±160・width70）。
    //   内側の輪 (±68) は安置 (ボスの周り0〜100px) を潰していたので廃止。
    // 🔵 2026-09-03 第3便 ユーザー裁定: 安置＝ボスの周り 0〜100px だけ。割れ目は110pxより外側だけで、
    //   外へ向かって0.2秒ずつ順に噴く（突き立て=chargeMs 1500 の0.2秒後から内側の輪、以後0.2秒刻み）。
    //   隙間なく110〜430pxを覆う（各width80）。突き立て（190ms・active）を回避で抜けて懐へ入るのが唯一の正解。
    // 🔵 2026-10-23 検収 kensyu-exec-quake-size-1023 Q-1「20がよい」: 後ろ側の最初の帯だけ 20px 外へ (-150 → -170・ボスの中心から 130〜210px)。
    //   奥寄りから前回避した着地が帯の内側の縁に触れ、無敵の音のあと当たっていた。次の帯 (190〜270px) と重なるので隙間は出ない。
    //   旧の位置は DEV ?quakeBack=old (src/exec_quake_back_gap.ts)。
    hazardSpawns: [
      { anchor: 'boss', offset: 150, width: 80, telegraphMs: 1700, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: -170, width: 80, telegraphMs: 1700, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: 230, width: 80, telegraphMs: 1900, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: -230, width: 80, telegraphMs: 1900, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: 310, width: 80, telegraphMs: 2100, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: -310, width: 80, telegraphMs: 2100, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: 390, width: 80, telegraphMs: 2300, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
      { anchor: 'boss', offset: -390, width: 80, telegraphMs: 2300, activeMs: 260,
        damage: 14, postureDamage: 22, hitOnce: true, kind: 'quake' },
    ],
  },
  // 発勁の突き — 作り直し (2026-09-09)。地割り (exec_maulQuake) の直後だけ繋がる専用技
  //   (譜面 exec-maul-quake-fajin・骨格表 EXECUTIONER_ATTACKS には乗せない = 抽選プール外)。
  //   逐語 (2026-09-09)＝「突きをしたあと衝撃波が出る。突きをした後は普通に他の技同様
  //   介錯は戦闘に戻る。それに関わらず衝撃波が飛んでくる」。
  //   chargeMs = 溜め (drawMs) だけ。一拍 (beatMs) はボスの待ちではなく波側の時間差
  //   = castProjectile.launchDelayMs (下)。2026-09-12「余韻を調整して」で戻りを140→300msへ。
  //   突き切り→刃と肩を下ろす→idle。既定の波760msを待たず通常AIへ戻る。
  //   🔴 recoveryMs 1000 (他の刃の技と同じ帯) は 2026-09-09 却下＝逐語「硬直が明けたら歩く、ここが違う。
  //   衝撃波は独立して制御してほしくて、普通に戦いに戻ってほしい」。
  //   溜め3択の切替は src/executioner_fajin.ts (setFajinDraw) が chargeMs を、一拍5択の切替は
  //   同ファイル (setFajinBeat) が launchDelayMs を書き換える (実機で詰める前提)。
  //   光波の見た目は既存の弾を流用 (便2でFXを作る)。
  //   🔴 同日いったん『ボスが待つ一拍』『突いた後の4択 (hold/drift/cut/cutDrift)』で作って却下
  //   ＝commit 1114768b。逆だった: ボスは待たず、波が刃先に留まって時間差で飛ぶ。
  //   正本 = docs/candidates/2026-09-07-executioner-fajin-thrust-motion-spec.md §6
  // Cannon follow-up: same independent delayed wave as the blade form.
  exec_cannonFajin: {
    id: 'exec_cannonFajin', label: '砲発勁', kind: 'projectile',
    chargeMs: 900, activeMs: 120, recoveryMs: 300,
    parryable: true, range: 0, damage: 24, postureDamage: 36,
    turnsDuringTelegraph: true, facingLockBeforeActiveMs: 140,
    crossingPolicy: 'not-applicable', crossingPolicyExemption: 'travel-projectile',
    sourceBossId: 'the-executioner',
    castProjectile: { speedPxPerSec: 1000, widthPx: 34,
      ...EXECUTIONER_BLADE_FAJIN_MUZZLE, descentDeg: 0, launchDelayMs: 760 },
  },
  exec_bladeFajin: {
    id: 'exec_bladeFajin',
    label: '発勁の突き',
    kind: 'projectile',
    chargeMs: 900,
    activeMs: 120,
    recoveryMs: FAJIN_RECOVERY_MS.settled,
    parryable: true,
    range: 0,
    damage: 24,
    postureDamage: 36,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'travel-projectile',
    sourceBossId: 'the-executioner',
    castProjectile: {
      speedPxPerSec: 1000,
      widthPx: 34,
      ...EXECUTIONER_BLADE_FAJIN_MUZZLE,
      descentDeg: 0,   // 水平に飛ぶ
      launchDelayMs: 760,   // 一拍＝波が刃先に留まる時間 (setFajinBeat が書き換える)
    },
  },

  // --- 骨格C: 踏み込んで伸ばす ---------------------------------------------
  // 刃。踏み込んで刃の腕を前へ突き出す。★突きなので弾けない = 見切り (回避で反撃窓が開く)。
  //   ⚠️ 骨格C は3種とも「弾けない」で統一する: 突き / 掴み / 転がし突進。
  //   これはこのゲーム全体の既存文法 (AttackDefense の evade-counter = 突き・突進・掴み) であって
  //   このボスだけの特例ではない。骨格Cの入り (前足を踏み出す) を見たら答えは常に回避、と読める。
  //   ⚠️ id は `exec_maulJab` のまま (セーブ/テスト/タイムラインに波及するため改名しない)。
  //   🔴 旧称「柄突き」は 2026-07-29 に無効: 武器の腕そのものが武器になり **柄が存在しない**。
  //      台帳 = docs/decision-ledger.md §常打ちの刑吏「核心＝腕が武器」
  //      ⚠️ ~~「右腕」~~ とは書かない (2026-08-22 裁定・台帳同日行)。画面では反転が常態なので、
  //         どちらの腕かは元絵の左右では決まらない。生きているのは「腕が武器」までで、左右は決めない。
  exec_maulJab: {
    id: 'exec_maulJab',
    label: '突き当て',
    kind: 'physical',
    chargeMs: 650,
    activeMs: 130,
    recoveryMs: 620,
    parryable: false,
    unblockable: true,
    preferClose: true,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 range 150 / strike{10,150} / approachDistancePx 150 / lungeDistance 55）
    range: 120,
    strike: { localFromPx: 8, localToPx: 120 },
    approachDistancePx: 120,
    // ★この技だけは「居座りへの罰」も兼ねる (retaliationAttackId / backAttackId)。
    //   罰の技が痛いと、殴りに行くこと自体が割に合わなくなる = 攻めの選択肢が死ぬ。
    //   低ダメージ + 位置避け可 = 「離れれば空振りが正しい」で、旧・蹴りが担っていた役をそのまま継ぐ。
    damage: 16,
    postureDamage: 22,
    lungeDistance: 44,
    positionEvadable: true,   // 密着の罰 = 追走キャッチアップをしない (2026-07-06 の裁定を継承)
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 110,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
  },

  // --- 新技3本 (2026-07-30・ユーザー指定) --------------------------------------
  // 票 = docs/candidates/2026-07-30-executioner-three-new-attacks-ticket.md
  // 台帳 = docs/decision-ledger.md §常打ちの刑吏「2026-07-30 セッション」§新技3本
  //
  // ■ なぜ足したか: 譜面13本で弾き成功は 37→50回 に増えたが、**刑吏には赤の連撃が1本も無く**
  //   「回避して反撃する」リズムが存在しなかった。技側を足さないと譜面だけでは届かない。
  //
  // ■ 3本とも**既存の骨格に相乗りする** (新しい骨格は作らない) = 台帳の「同じ入り・違う終わり」。
  //   相乗り先で正解防御が揃っていることが条件 (src/executioner_weapon.ts EXECUTIONER_ATTACKS 参照)。
  //
  // ■ 公平性の床 (実データから引いた・票 §2)
  //   ・弾ける連撃の comboGapsMs は **320ms 未満にしない** (PLAYER.parryActiveMs 200 + parryRecoveryMs 120
  //     = 弾き直しの最短サイクル)。下回ると物理的に弾き直せない = 理不尽。
  //   ・「回避 → 反撃」1組の成立には **約640ms** 必要 (stepIFrameMs 200 + 振り切り 400 + 余白)。
  //     PLAYER.counterWindowMs 550 は「振り始められる」長さで「振り終わる」長さではない。
  //   ・赤のその場の一撃は無敵の長さに当たりを詰める (短い無敵の比較版で hits.ts が適用)。
  //     10-07 ユーザー「案1 走り終わりまで当たる」: 覚醒王の走る赤は例外。走り終わりまで脅威を残し、最初の接触1回だけ当たる。
  //
  // ⚠️ deprecated な `defense` フラグは使わない (hit-timeline 移行で撤去済)。
  //    見切りは hits.ts:310 の `parryable===false && follow==='boss'` から創発する。
  // ⬜ 数値は暫定。tests/combat_sim/economy_probe.test.ts の実測で詰める。

  // 刃・骨格A に相乗り。3秒級の長溜め・弾ける単発。
  //   ★役割 = 譜面の「間」を作る。3秒の溜めは殴りに行く時間そのもの = **攻めの誘い**。
  //   🔴 旧「見分けは auraTint のチャージオーラ」は却下済み。
  //   オーラは「長いぞ」は言えても「いつ」は言えないため撤去する。
  //   出所 = docs/decision-ledger.md「ディレイ（拍をずらす技）の作り方」
  //   軌道は横薙ぎ。正本 = docs/candidates/2026-08-26-executioner-parry-three-motions-design.md §3c
  // 🔵 2026-08-28 ユーザー裁定「パリィ可能な3連斬りにして」= 遠距離側 (急接近後の斬り) を
  //   単発横薙ぎ→3連へ変更。正本 = docs/candidates/2026-08-28-executioner-lunge-triple-slash.md。
  //   comboStages の書式は exec_yokePull (直上・comboHits:3) に揃えた = 段0/1は base 値のまま、
  //   段2だけ comboStages で上書き。damage/postureDamage の配分は票 §1 のとおり
  //   (段0/1=D*0.4・P*0.5 / 段2=D*0.5・P*0.6、D=14・P=48 は旧単発の値)。
  //   comboGapsMs 360ms は 320ms (弾き再サイクル) の床を割らない。telegraph の拍・急接近は不変。
  exec_maulSet: {
    id: 'exec_maulSet',
    label: '据えの一打',
    kind: 'physical',
    chargeMs: 2900,         // ★刑吏で最長。溜めきる前に殴りに来させる
    activeMs: 200,
    recoveryMs: 1500,       // ★このボス最長の隙 (振り下ろし1200を上回る) = 待ち切った報酬
    parryable: true,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 range 200 / strike{0,200} / approachDistancePx 150 / lungeDistance 45）
    range: 160,
    strike: { localFromPx: 0, localToPx: 160 },
    approachDistancePx: 120,
    damage: 14,             // 段0/1 の値。段2は comboStages で18へ上書き（正本 = docs/candidates/2026-09-03-executioner-combat-numbers-fable5-design.md §3）
    postureDamage: 26,      // 段0/1 の値。段2は comboStages で32へ上書き
    comboHits: 3,
    comboGapsMs: [360, 360],   // 弾き再サイクル320ms超・初見の3連として読める側(6連340msより一拍ゆったり)
    comboStages: [{}, {}, { damage: 18, postureDamage: 32 }],   // 段2だけ締めとして重い
    lungeDistance: 36,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 200,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
    // 🔵 2026-08-27 夜 ユーザー裁定 = 分岐技（一心式・旧C6 と同じ骨）。
    //   居座らなければ溜め末尾 240ms でプレイヤー手前 130px まで一気に詰めて横薙ぎ（弾ける）。
    //   🔴 旧「じりじり詰め」(telegraphCreepDistance 90) は外した = 実機で知覚できず（検収パック 2026-08-27 夜 B-1）。
    // 🔴 2026-08-31 距離分岐は廃止（実プレイ裁定=距離はこのゲームの軸として偽物）→ greedSwitch へ。
    //   正本 = docs/candidates/2026-08-31-executioner-greed-branch-design.md
    // proximitySwitch: { toAttackId: 'exec_maulSetPierce', withinPx: 170, sustainMs: 600, atMs: 1200 },
    // 欲張り分岐 (2026-08-31 ユーザー裁定「Aで」): 溜め中にプレイヤーの攻撃が hits 回このボスへ
    //   ダメージ成立したら即座に exec_maulSetPierce（赤い突き）へ切り替える。
    //   hits=4 は仮値（実プレイで詰める。溜め2.9秒で殴れるのはおよそ3〜5発の想定）。?setGreed=<n> で上書き可。
    greedSwitch: { toAttackId: 'exec_maulSetPierce', hits: 4 },
    preActiveLungeMs: 240,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 420。記載漏れの追補・コーディネーター裁定で確定）
    preActiveLungeDistance: 336,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 130）
    preActiveLungeStopGapPx: 104,
  },
  // ひねり薙ぎの欲張り派生 (2026-08-31 ユーザー裁定「Aで」)。溜め中に殴られた回数が
  //   exec_maulSet.greedSwitch.hits を超えると greedSwitch でここへ切り替わる。
  //   赤 = 弾けない。位置避け可 = 離脱と回避が正解。
  //   通常プールには入れない（派生専用）。数値 (chargeMs/activeMs) は実機で詰める前提の仮置き。
  exec_maulSetPierce: {
    id: 'exec_maulSetPierce',
    label: '据えの突き',
    kind: 'physical',
    // 🔵 2026-08-30 実プレイ A-2『もうワンテンポ早く』で 450→350
    // 🔵 2026-10-09 ユーザー採用「v3（溜め600）で本番へ」で 350→600 (切り替わりで突きの構えへ替わるのを見てから避けられる長さ)。
    //   旧 350 は DEV ?braceThrust=old (src/exec_brace_thrust.ts の BRACE_THRUST_OLD_CHARGE_MS)。
    chargeMs: 600,
    activeMs: 140,
    // 🔵 2026-08-31 読み勝ちの報酬（設計票 §仕様3）＝突きを避け切った隙をこのボス最長級へ。
    //   950→1500 (振り下ろし1200を上回る＝据えの一打 recoveryMs と並ぶ最長級)。
    recoveryMs: 1500,
    parryable: false,
    unblockable: true,
    preferClose: true,
    // 🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 range 210 / strike{0,210} / approachDistancePx 210 / lungeDistance 60）
    range: 168,
    strike: { localFromPx: 0, localToPx: 168 },
    approachDistancePx: 168,
    damage: 26,
    postureDamage: 32,
    lungeDistance: 48,
    positionEvadable: true,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 80,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
  },

  // ===== 腕砲3本（フェーズ1。数値はすべて⬜ドラフト） =====
  exec_cannonShot: {
    id: 'exec_cannonShot',
    label: '直射弾',
    kind: 'projectile',
    chargeMs: 900,           // ⬜ドラフト
    activeMs: 120,           // ⬜ドラフト（発射判定の拍。弾自体は壁まで飛ぶ）
    recoveryMs: 800,         // ⬜ドラフト
    parryable: true,
    range: 0,
    damage: 24,
    postureDamage: 32,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 140,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'travel-projectile',
    sourceBossId: 'the-executioner',
    castProjectile: {
      speedPxPerSec: 620,    // ⬜ドラフト
      widthPx: 34,           // ⬜ドラフト。🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 42）
      ...EXECUTIONER_CANNON_MUZZLE,
      // 構えの砲身の傾き。弾はこの角度で直進し床で消える
      // 🔵 2026-09-03 第4便: 400px 先で膝の高さ（setExecutionerCannonRange で追従）
      descentDeg: 10,
    },
  },
  exec_cannonVolley: {
    id: 'exec_cannonVolley',
    label: '3連速射',
    kind: 'projectile',
    chargeMs: 850,           // ⬜ドラフト
    activeMs: 100,           // ⬜ドラフト
    recoveryMs: 950,         // ⬜ドラフト
    parryable: true,
    range: 0,
    damage: 14,              // 1発ごと
    postureDamage: 22,       // 1発ごと
    comboHits: 3,
    comboGapsMs: [420, 650], // ⬜ドラフト: 320msの弾き直し下限を割らない。🔵 2026-09-03 第3便 ユーザー裁定: 3発目の前だけ間を伸ばす（旧 420）
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 120,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'travel-projectile',
    sourceBossId: 'the-executioner',
    castProjectile: {
      speedPxPerSec: 720,    // ⬜ドラフト
      widthPx: 29,           // ⬜ドラフト。🔵 2026-09-02 夜 等身 1.6 化で 0.8 倍（旧 36）
      ...EXECUTIONER_CANNON_MUZZLE,
      // 構えの砲身の傾き。弾はこの角度で直進し床で消える
      // 🔵 2026-09-03 第4便: 400px 先で膝の高さ（setExecutionerCannonRange で追従）
      descentDeg: 10,
    },
  },
  exec_cannonMortar: {
    id: 'exec_cannonMortar',
    label: '山なり榴弾',
    kind: 'area',
    // 🔵 2026-09-03 第4便 ユーザー裁定「落ちてきている間には次の行動に移っている状態にしたい」:
    //   撃つ 450ms (CANNON_MORTAR_DEPARTURE_MS) → 技は 700ms で終わり (旧1400)、着弾は
    //   hazardSpawns の telegraphMs 2000ms へ独立して伸ばす。撃ったらすぐ次の行動へ移れる。
    chargeMs: 700,
    activeMs: 100,
    recoveryMs: 450,
    parryable: false,
    unblockable: true,       // パリィ不可。榴弾の体は赤くしない（床の位置予告で伝える）
    range: 0,
    damage: 0,               // 当たりは床ハザードだけ
    postureDamage: 0,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 180,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'floor-hazard',
    sourceBossId: 'the-executioner',
    // 🔵 2026-10-02 ユーザー裁定 (通しパック P-2「理不尽」→ A＋B): 1回の榴弾 (3列) からは1発だけ。
    hazardHitOncePerCast: true,
    // 🔵 2026-09-03 第3便 ユーザー裁定 (案A): 主人公中心に横3列 (offset -110/0/+110・幅90) へ
    //   自由落下。列の隙間は20px＝立ち止まると当たる・走れば抜ける。旧は1本 (offset0・width150)。
    // 🔵 2026-09-03 第4便: telegraphMs 1400→2000 (技の尺 700ms から独立させ、着弾は変えない)。
    // 🔴 2026-09-05「榴弾から逃げるのがタダ」への 5列化は**不採用**（既定は3列のまま）。
    //   理由＝番人2本が割れた。①tests/hazard_escape.test.ts＝壁際で 281px 必要 > 上限 251px
    //     （2026-06-11 ユーザー裁定「床技は最悪の立ち位置でも回避1回+歩きで予告の8割以内に抜けられる」）。
    //     帯の全幅が 350px→530px になり、壁で片側を潰されると逃げ切れない＝理不尽になる。
    //   ②tests/combat_sim/boss_measurement.test.ts＝弾き主体 bot の勝率が 14/20→0/20（必須床0.5割れ）。
    //   比較用に 5列は切替として残す（?mortarRows=5 / __setMortarRows(5)）。既定を変える時は上の2本を先に通すこと。
    hazardSpawns: [
      { anchor: 'player', offset: -110, width: 90, telegraphMs: 2000, activeMs: 360,
        damage: 20, postureDamage: 32, hitOnce: true, kind: 'quake' },
      { anchor: 'player', offset: 0, width: 90, telegraphMs: 2000, activeMs: 360,
        damage: 20, postureDamage: 32, hitOnce: true, kind: 'quake' },
      { anchor: 'player', offset: 110, width: 90, telegraphMs: 2000, activeMs: 360,
        damage: 20, postureDamage: 32, hitOnce: true, kind: 'quake' },
    ],
  },
  // 砲身薙ぎ (2026-09-03 第4便 ユーザー「砲形態で密着していたら砲身で薙ぐ（パリィ可能）」)。
  //   密着 (cannonRangeTuning().sweepWithinPx=150) への返し。抽選には乗らない (boss.ts の idle 末尾が強制選択)。
  //   ⬜ 絵は仮 (既存の mortar_aim / fire_recoil を回して代用)。動きが通ってから発注。
  exec_cannonSweep: {
    id: 'exec_cannonSweep',
    label: '砲身薙ぎ',
    kind: 'physical',
    chargeMs: 600, activeMs: 140, recoveryMs: 900,
    parryable: true,
    range: 150,
    strike: { localFromPx: -20, localToPx: 150 },
    approachDistancePx: 150,
    damage: 18, postureDamage: 30,
    // 🔵 2026-09-03 第5便: knockbackPx だと無害な押し出しになる (hits.ts の最優先分岐)。
    // 🔵 2026-09-04 噛み合わせ (実プレイ「砲形態が薙ぎループで死ぬ」): 当てた時 120→240 (薙ぎの届く 150 の外へ)。
    //   hitKnockbackPx はモードに関わらず共通 (被弾時の押し出し)。
    // 🔵 2026-09-06 ユーザー裁定 (実プレイ検収): 案2「弾いたら殴れる」='duel' が既定になった＝
    //   parryKnockbackPx は書かない (undefined＝弾いても押さない)。旧既定 'shove' の 240 は
    //   EXECUTIONER_CANNON_SWEEP.shoveParryKnockbackPx に残し、?cannonMode=shove / setExecutionerCannonMode
    //   が選ばれた時だけここへ書き戻す (ここは既定の写し＝既定を変えたら必ずこの値も合わせる)。
    //   正本 = docs/candidates/2026-09-04-executioner-cannon-tempo-design.md
    hitKnockbackPx: 240,
    lungeDistance: 0,
    turnsDuringTelegraph: true,
    facingLockBeforeActiveMs: 150,
    crossingPolicy: 'track-until-commit',
    sourceBossId: 'the-executioner',
  },

  // ========== 覚醒王 (crowned-king-awoken) ==========
  // 2026-07-07 Fable5 ビルド (⬜候補・数値は全部ドラフト=検収パックで実測):
  //   台帳ピン「卒業試験=同じ読みを速く+苛烈型 (耐えて耐えて、たまに通る)」+
  //   ヒアリング「穴の解読が本体 (差し込める所が分からないと被弾する)」を 9本+致命 で実装。
  //   罠2種 (伸び/追い噛み) = 偽物の穴の出題。罠技ほど後の隙が深い = 読み切りの報酬 (柱2)。
  //   設計正本: docs/candidates/2026-07-05-crowned-king-awoken-motion-komawari-pack.md +
  //   docs/reports/2026-07-07-awoken-king-fable5-build-state.md (D1-D7)。
  feralPocketSpin: {
    id: 'feralPocketSpin', label: '懐払い', kind: 'physical',
    // 10-02 5枚版: 見本GIFの時間割（溜め500＝wind430+rear70・当たり=sweep の絵が出ている間だけ(active 0〜100ms=500〜600ms)・front の絵(向き直り)には当たりなし(ユーザー指示 10-02)・active は振り抜き100+向き直り前70）。
    //   旧は 520/420/560・当たり 120〜300。戻り 560 は据え置き（隙は変えない）。数値は検収で詰める。
    chargeMs: 500, activeMs: 170, recoveryMs: 560,
    parryable: true, defense: 'parry', range: 140, approachDistancePx: 140,
    crossingPolicy: 'rear-coverage',
    strike: { localFromPx: -230, localToPx: 140 }, meleeLiveWindowMs: [0, 100],
    damage: PLAYER_DAMAGE.standard, postureDamage: 42,
    sourceBossId: 'crowned-king-awoken',
  },
  feralBite: {
    id: 'feralBite',
    label: '暴噛',
    kind: 'physical',
    chargeMs: 480,    // ★houndBite(650) より速い。 弾きの速度試験
    activeMs: 140,
    recoveryMs: 520,
    parryable: true,
    defense: 'parry',
    range: 130,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 42,
    lungeDistance: 150,
    sourceBossId: 'crowned-king-awoken',
  },
  feralRush: {
    id: 'feralRush',
    keepHitWhileTravelling: true,
    label: '暴走突進',
    kind: 'physical',
    chargeMs: 700,
    activeMs: 260,
    recoveryMs: 800,   // 見切れば騎士譲りの攻め時
    parryable: false,
    defense: 'evade-counter',
    unblockable: true,   // 裁定C: 突進=弾けない=赤
    range: 150,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 40,
    lungeDistance: 330,  // houndLunge(260) より深い
    sourceBossId: 'crowned-king-awoken',
  },
  // 大暴れ: 跳ねて床を踏み砕く。 前方の床が爆ぜる = 離れて待つ (evade)。
  //   2026-06-11 公平性修正: 幅320/予告800 は帯中央から両出口とも175px = 回避不能だった。
  //   幅240/予告850 で最遠135px ≤ 移動上限140px に。
  rampage: {
    id: 'rampage',
    label: '大暴れ',
    kind: 'area',
    chargeMs: 850,
    activeMs: 500,
    recoveryMs: 950,
    parryable: false,
    defense: 'evade',
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 34,
    sourceBossId: 'crowned-king-awoken',
    hazardSpawns: [{
      // 帯=[王+100..王+290]。 内側100px = 壁際×間合い上限からでも懐へ抜けられる
      // 🔴 2026-08-10: offset 170 → 195。**ボスの立ち位置を 150→200 へ下げたのに追随させた変更**で、
      //   帯の太さ(190)も「プレイヤーから見た帯の位置」も変えていない (ボスが25px下がり帯も25px下がる)。
      //   実際に変わるのは「王の足元寄りの安全地帯が25px広い」ことだけ。
      //   ⚠️ この offset と attackCommitRange は連動する。逃走に必要な距離 =
      //   (commitRange+10) - (offset - width/2) + プレイヤー半幅15 ≤ 140 (tests/hazard_escape)。
      //   offset 195 / width 190 なら commitRange の天井は 215。いまは 210 (5px の余裕)。
      anchor: 'boss', offset: 195, width: 190, telegraphMs: 850, activeMs: 500,
      damage: PLAYER_DAMAGE.standard, postureDamage: 34, hitOnce: true, kind: 'shock',
    }],
  },
  // 大暴れ第5案 (2026-09-19 ユーザー指定・09-21 に配布版の既定へ入れ替えた＝src/awoken_rampage_v5.ts。旧の rampage は DEV `?rampage=old` だけ)。
  //   部屋を跳び回り、噛みつき約10回 (横切りは1.35秒おき)＋怨念を撒き、最後に疲れ果てて倒れ込む。台本＝awoken_rampage_choreo.ts の ROAM。
  //   range:0 = 共通の近接ヒットは出さない (胴は無害)。噛みつきは game.ts が台本の窓で出す。
  //   recoveryMs = 倒れ込み＝反撃の時間。数値はモックの仮値 (駅4で詰める)。
  //   設計＝docs/handoffs/2026-09-19-awoken-rampage-v5-implementation-ticket.md §5
  rampageV5: {
    id: 'rampageV5',
    label: '大暴れ',
    kind: 'physical',
    chargeMs: CHOREO_CHARGE_MS,
    activeMs: CHOREO_ACTIVE_MS,
    recoveryMs: 1600,
    parryable: true,
    defense: 'parry',
    range: 0,
    choreography: 'awokenRampageV5',
    // 噛みつきの向きは各構えの頭で固定する＝構え中に裏へ回れば空振りする (意図した抜け道)。
    crossingPolicy: 'intentional-opening',
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 42,
    sourceBossId: 'crowned-king-awoken',
  },
  // 暴連噛: 3連の噛みつき = 苛烈型の手数の主役 (押し込まれながら受け合う体感の中心)。
  //   gap 320ms = パリィ1サイクル (startup0+active200+recovery120) と同拍 = 「弾き続けられる」下限。
  //   per-hit damage は 20→14 に抑制 (3連フルヒット60は即死圧すぎる。圧は体幹側が担う)。
  feralFrenzy: {
    id: 'feralFrenzy',
    label: '暴連噛',
    kind: 'physical',
    chargeMs: 750,
    activeMs: 130,
    recoveryMs: 850,
    parryable: true,
    defense: 'parry',
    range: 135,
    collisionDraft: true,
    damage: 14,
    postureDamage: 30,
    lungeDistance: 130,
    comboHits: 3,
    comboGapMs: 320,
    sourceBossId: 'crowned-king-awoken',
  },
  // 暴連噛・伸び (罠 of 暴連噛): 3連と全く同じ入り→3拍目の後、後肢が再び沈んで +1拍 (計4連)。
  //   「3連終わった=今だ」の早い差し込みを刺す。見分け=3拍目後の gap が長い (500ms) 間に
  //   extend_coil (後肢の沈み直し) が出る。通常の3連は息継ぎ (recovery) に入る。
  //   読み切った報酬: recovery 1100 = 通常 (850) 比 +30% の深い穴 (柱2「罠の奥に本物の穴」)。
  feralFrenzyExtend: {
    id: 'feralFrenzyExtend',
    label: '暴連噛・伸び',
    kind: 'physical',
    chargeMs: 750,          // ★暴連噛と同一 = 入りでは見分けられない (それが出題)
    activeMs: 130,
    recoveryMs: 1100,
    parryable: true,
    defense: 'parry',
    range: 135,
    collisionDraft: true,
    damage: 14,
    postureDamage: 30,
    lungeDistance: 130,
    comboHits: 4,
    comboGapsMs: [320, 320, 500],   // 3拍目→4拍目だけ長い = extend_coil サインの窓
    comboStages: [{}, {}, {}, { damage: 18, postureDamage: 38 }],   // 罠の1発は重い=1回で分からせる
    sourceBossId: 'crowned-king-awoken',
  },
  // 暴噛・溜め (リズム外し): 暴噛と同じ構えのまま、噛む瞬間だけ一拍遅い (charge 480→880)。
  //   速度に体が慣れた頃 (幕2〜) に「暗記でなく反応」へ戻す。新規絵ゼロ (windup 保持 ~1.8倍)。
  feralBiteDelayed: {
    id: 'feralBiteDelayed',
    label: '暴噛・溜め',
    kind: 'physical',
    // 2026-09-12 検収E-2: 比較候補1300msを採用。初回の押し違いから押し直せる溜め。
    // 入りの共有姿勢87/150ms・見分けの沈み300msはタイムライン側で維持。
    chargeMs: 1300,
    activeMs: 140,
    recoveryMs: 560,
    parryable: true,
    defense: 'parry',
    range: 130,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 42,
    lungeDistance: 160,
    sourceBossId: 'crowned-king-awoken',
  },
  // 追い噛み (罠 of 暴走突進・低頻度): 突進の伸び切りから牙が向き直り、二の噛みが飛ぶ。
  //   「唯一の攻め時 (突進 recovery)」にすら罠を1枚 = 本物の隙を最後まで見極めさせる。
  //   実装 = 突進と同一の telegraph/active を持つ 2hit combo (入りでは見分け不能が正)。
  //   見分け = hit0 の後の comboGap (620ms) 中に chase_coil (牙の向き直り) が出る。
  //   本物 (feralRush) は recovery で rush_overextend (頭が泳ぐ) のまま。
  //   読み切った報酬: recovery 1150 = 全技最長の穴。
  feralRushChase: {
    id: 'feralRushChase',
    keepHitWhileTravelling: true,
    label: '追い噛み',
    kind: 'physical',
    chargeMs: 700,          // ★feralRush と同一 = 入りでは見分けられない
    activeMs: 260,
    recoveryMs: 1150,       // 全技最長 (罠の奥の本物の穴)
    parryable: false,
    defense: 'evade-counter',
    unblockable: true,      // 裁定C: 突進系=弾けない=赤 (両ヒットとも回避)
    range: 150,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 40,
    lungeDistance: 330,
    comboHits: 2,
    comboGapsMs: [620],     // 伸び切り(偽の穴) → 牙の向き直り(サイン) の窓
    comboStages: [{}, { keepHitWhileTravelling: false, range: 140, damage: 16, postureDamage: 36 }],
    sourceBossId: 'crowned-king-awoken',
  },
  // 壁跳ね・往 (大技・速さの最大出力 1/2): 低い疾走の構えから、アリーナを端まで裂くように疾走。
  //   positionEvadable = プレイヤーを追わない直進 = 通過の瞬間を回避 (i-frame) するか、
  //   走路の後ろ側 (発進点より手前) に立つ。※komawari の「2往復目=画面外」は不採用
  //   (場外移動はエンジン非対応・公平性リスク > 演出価値。往復とも画面内で成立させる)。
  feralWallDash: {
    id: 'feralWallDash',
    keepHitWhileTravelling: true,
    label: '壁跳ね・往',
    kind: 'physical',
    chargeMs: 720,
    activeMs: 620,
    recoveryMs: 500,        // string では beatGap に置換される (単発時の保険値)
    parryable: false,
    defense: 'evade',
    unblockable: true,      // 裁定C: 弾けない疾走=赤
    positionEvadable: true, // 直進 (ホーミングしない) = 走路そのものが出題
    range: 90,
    collisionDraft: true,
    damage: 18,
    postureDamage: 36,
    lungeDistance: 780,     // 780/620 px/ms で実際の壁まで進む
    passThroughBody: true,
    wallContactInsetPx: 106, // wall_kick の後ろ足: (973/2) * (186/850) ≈ 106px
    sourceBossId: 'crowned-king-awoken',
  },
  // 壁跳ね・復 (大技 2/2): 壁を蹴って向き直り (turnsDuringTelegraph)、同じ速さで戻ってくる。
  //   戻りの後は rush_overextend の大きな伸び切り = 大技を見切った者への最大の反撃窓。
  //   ⚠️ この技ファミリーの正解は「通過の瞬間を回避 (i-frame タイミング)」— 走路がアリーナ幅に
  //   迫るため歩きの位置どりでは避け切れない (発進点より後方に立つ部分回避のみ)。
  //   10-07 案1: 無敵100msでも最初の接触を凌げば当たりは消費される。全身の通過を無敵で覆う必要はない。
  //   charge 480→560: 実測で唯一の被弾源だった読み窓を +80ms (justParryBot 検証・アニメレビューP0)。
  feralWallDashBack: {
    id: 'feralWallDashBack',
    keepHitWhileTravelling: true,
    label: '壁跳ね・復',
    kind: 'physical',
    chargeMs: 560,
    activeMs: 620,
    recoveryMs: 1150,       // 大技の締め = 深い穴
    parryable: false,
    defense: 'evade',
    unblockable: true,
    positionEvadable: true,
    passThroughBody: true,
    wallContactInsetPx: 106,
    reboundFromWall: true,
    turnsDuringTelegraph: true,   // 壁蹴りの間にプレイヤーへ向き直る
    range: 90,
    collisionDraft: true,
    damage: 18,
    postureDamage: 36,
    lungeDistance: 780,
    sourceBossId: 'crowned-king-awoken',
  },
  // 軋む咆哮 (フェーズ移行技・幕3開幕の合図): 頭を上げ胸を張る最大の縦 → 同心の衝撃波 3波。
  //   処刑人 G11 と同型の roarWave hazard 部品で安く乗る (波形パラメタは G11 実証値を踏襲)。
  //   正解 = 位置どり。安全地帯は「懐 (|dx|<60)」と「最外波の外 (>240)」の両方 (波は内→外へ
  //   連結展開するので、懐へ潜るのが最短の正解。G11 と同じ)。ゾーン慣例により赤は付けない。
  feralRoar: {
    id: 'feralRoar',
    label: '軋む咆哮',
    kind: 'area',
    chargeMs: 2900,         // 2100→2900 (2026-07-07 パックA-6「もっと長く・しっかり切り替わったように」)
    activeMs: 300,
    recoveryMs: 900,
    parryable: false,
    defense: 'evade',
    range: 0,               // 判定の本体はゾーン (melee ヒットなし)
    damage: 0,
    postureDamage: 0,
    sourceBossId: 'crowned-king-awoken',
    hazardSpawns: [
      // 波1 (内側・最初) — [王±60〜±120], 発火 t=1900 (幾何は G11 実証形・時間は咆哮の尺に合わせ延長)
      { anchor: 'boss', offset: 90, width: 60, telegraphMs: 1900, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      { anchor: 'boss', offset: -90, width: 60, telegraphMs: 1900, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      // 波2 — [王±120〜±180], 発火 t=2300
      { anchor: 'boss', offset: 150, width: 60, telegraphMs: 2300, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      { anchor: 'boss', offset: -150, width: 60, telegraphMs: 2300, activeMs: 250,
        damage: 10, postureDamage: 15, hitOnce: true, kind: 'roarWave' },
      // 波3 (外側・最後) — [王±180〜±240], 発火 t=2700
      { anchor: 'boss', offset: 210, width: 60, telegraphMs: 2700, activeMs: 250,
        damage: 14, postureDamage: 20, hitOnce: true, kind: 'roarWave' },
      { anchor: 'boss', offset: -210, width: 60, telegraphMs: 2700, activeMs: 250,
        damage: 14, postureDamage: 20, hitOnce: true, kind: 'roarWave' },
    ],
  },
  kingExecutionAwoken: {
    id: 'kingExecutionAwoken',
    label: '暴噛・終',
    kind: 'physical',
    chargeMs: 450,
    activeMs: 250,
    recoveryMs: 750,
    parryable: false,
    defense: 'evade',
    range: 140,
    collisionDraft: true,
    // 32 = 覚醒王専用 (共有 finisher 60 から減額・王クローズの前例 houndExecution=25 と同型)。
    //   2026-07-07 パックA-1「ジャスパ1回ミスで即死級」対応: 苛烈型は崩される頻度が高いので、
    //   崩し1回=命灯1.6個に抑えて「痛いが立て直せる」へ。⬜ドラフト (次パックで再判定)。
    damage: 32,
    bossFinisher: true,
    postureDamage: 30,
    lungeDistance: 140,
    sourceBossId: 'crowned-king-awoken',
  },

  // ========== 終わらぬ鬨 (鶏 / id=inverted-jester) ==========
  // 一戦の部屋の構成 = src/rooster_fight.ts。前の道化の版の技 (鉤爪の連 短・長・羽刃の雨・急降下突き) は 10-29 に
  //   LUNAIRE-notes の archived/2026-10-29-rooster-jester-version/ へ退避した (戻し方＝そこの README)。

  // 暁呼び (駅0′の仮・2026-10-17・ラボ専用＝本編のプールに入れない): 鳴いて翼を広げる予告 → 地上で爪の3連 (弾く)、
  //   同じ拍で羽の雨が主人公の後ろへ1枚ずつ降り、下がる道を塞ぐ (避ける)。「その場で弾き切れ」の二層同時の最小形。
  //   エンジンの追加は要らない: 振り (comboHits) と床の帯 (hazardSpawns) は別々に動く (前例＝花嫁 veilInvitation・剣舞 hemSweep)。
  //   帯は telegraph 突入時のボスの位置から 330/450/570 (爪の届く 130+140=270 より奥)。telegraphMs を爪の拍に揃えて 950/1310/1670。
  //   数値は全て仮。メモ docs/research/2026-10-17-rooster-station0-prep.md §3。
  dawnCallClawStudy: {
    id: 'dawnCallClawStudy',
    label: '暁呼び（仮・爪と羽の雨）',
    kind: 'physical',
    chargeMs: 950,
    activeMs: 130,
    recoveryMs: 900,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 24,
    lungeDistance: 130,
    comboHits: 3,
    comboGapMs: 360,
    sourceBossId: 'inverted-jester',
    hazardSpawns: [330, 450, 570].map((offset, i) => ({
      anchor: 'boss' as const, offset, width: 110, telegraphMs: 950 + i * 360, activeMs: 300,
      damage: PLAYER_DAMAGE.standard, postureDamage: 20, hitOnce: true, kind: 'light' as const,
    })),
  },
  // 鬨ずらし (駅0′の仮・2026-10-18・ラボ専用＝本編のプールに入れない): もりしさんの看板技 (10-17 答えるページ)。
  //   「鳴く溜めの長さを変えて、爪の拍をずらす」。入りの鳴きは同じ形のまま、鳴きの長さだけが違う＝鳴き止んだ所で振ってくる。
  //   見るべき合図は「鳴き始め」ではなく「鳴き止み」。決め打ちの押し (いつもの間) は短い方で遅れ、長い方で早すぎる。
  //   10-30 作り直し (作者 R-1「なきやんでから攻撃開始するまでが早すぎて反応できない」「鳴き声によって弾くリズムが異なる感じに」):
  //     鳴き止みから1発目の当たりまで 450ms (前は約 200ms＝人が見てから押せない)。鳴き声の形が爪の拍をそのまま先に聞かせる。
  //     短い鳴き＝「コッ・コッ・コッ」の3声 → 爪も同じ間の3連「タ・タ・タ」。長い鳴き＝「コケコッコーー」→ 爪は「タ……タタ」(1発目の後に長くため、残り2発は詰める)。
  //     時間の定数と鳴きの声の時刻＝src/rooster_toki.ts。記録 docs/research/2026-10-30-rooster-toki-rhythm.md。
  tokiZurashiShort: {
    id: 'tokiZurashiShort',
    label: '鬨ずらし・短い鳴き（仮）',
    kind: 'physical',
    chargeMs: TOKI_SHORT_CHARGE_MS,
    activeMs: 130,
    recoveryMs: 680,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 24,
    lungeDistance: 130,
    comboHits: 3,
    comboGapMs: TOKI_SHORT_GAP_MS,
    sourceBossId: 'inverted-jester',
  },
  tokiZurashiLong: {
    id: 'tokiZurashiLong',
    label: '鬨ずらし・長い鳴き（仮）',
    kind: 'physical',
    chargeMs: TOKI_LONG_CHARGE_MS,
    activeMs: 130,
    recoveryMs: 680,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 24,
    lungeDistance: 130,
    comboHits: 3,
    comboGapMs: TOKI_LONG_GAPS_MS[0],
    comboGapsMs: [...TOKI_LONG_GAPS_MS],
    sourceBossId: 'inverted-jester',
  },
  // 鉤爪の連・鳴き挟み (同・ラボ専用): 「たた・(鳴く)・たた」。2発目のあとで一度鳴き直し、3発目を 900ms 遅らせる。
  //   鬨ずらしを連の途中に持ち込んだ形。連打で押し続けると3発目の前で押し切れずに食らう (剣舞の waltz と同じ罠の型)。
  //   鳴き直しの絵は未定 (間の 900ms は今は構えたまま止まって見える)。
  clawCrowBreak: {
    id: 'clawCrowBreak',
    label: '鉤爪の連・鳴き挟み（仮）',
    kind: 'physical',
    chargeMs: 700,
    activeMs: 130,
    recoveryMs: 900,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 22,
    lungeDistance: 130,
    comboHits: 4,
    comboGapsMs: [320, 900, 320],
    comboGapMs: 320,
    sourceBossId: 'inverted-jester',
  },
  // 宙廻り (同・ラボ専用・10-18): もりしさん (10-17)「飛んでから左右に飛び回りながら振らせてくる系の攻撃も入れたい」。
  //   溜め (chargeMs) = 飛び上がり → 宙を左右に2回跳ぶ → 止まって目が白く光る → 斜め下へ突っ込む。位置と高さは
  //   src/rooster_aerial_roam.ts の台本 (定数表 ROOSTER_ROAM) が決める。溜めの長さが着地の時刻 (立ち位置によらず一定)。
  //   着いた瞬間が1発目 (その場で爪・弾ける)、続けて前へ走って爪が2発目 (普通の踏み込み・弾ける)。2発目の後の戻りが斬れる隙。
  //   宙 (高さ 72 より上) にいる間は主人公の攻撃が当たらない (combat.ts の高さの判定)。宙では当たりを出さない。
  //   数値は全て仮。設計 docs/research/2026-10-18-rooster-aerial-and-crow-art-design.md §1。
  roosterAerialRoam: {
    id: 'roosterAerialRoam',
    label: '宙廻り（仮）',
    kind: 'physical',
    chargeMs: ROOSTER_ROAM_CHARGE_MS,
    activeMs: 130,
    recoveryMs: 900,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 24,
    lungeDistance: 130,
    comboHits: 2,
    comboGapMs: 300,
    choreography: 'roosterAerialRoam',
    sourceBossId: 'inverted-jester',
  },
  // 暁呼び (10-29・作者の答え r1「縦を使う・位置で避けるか判断で弾くか」): 鳴いて真上へ上がり、宙で止まって羽を3回降らせる。
  //   羽は game.ts が狙う秒に主人公の位置へ置く床の帯 (roosterFeather・弾いても避けても良い)。宙 (高さ 72 より上) は斬れない。
  //   降りた瞬間に翼で打つ (近い所だけ・弾ける)。戻りが長い＝降りた所が攻め時。台本＝src/rooster_vertical.ts。数値は全て仮。
  roosterDawnCall: {
    id: 'roosterDawnCall',
    label: '暁呼び',
    kind: 'physical',
    chargeMs: ROOSTER_DAWN_CHARGE_MS,
    activeMs: 140,
    recoveryMs: 1100,
    parryable: true,
    defense: 'parry',
    range: 120,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 30,
    strike: { localFromPx: -90, localToPx: 95 },   // 翼を左右へ打つ (後ろにも届く)
    choreography: 'roosterDawnCall',
    sourceBossId: 'inverted-jester',
  },
  // 羽 (暁呼びが降らせる帯の1本)。getAttack で引けるよう表にも置く (出す時は rooster_vertical.ts が本数を組む)。
  roosterFeather: roosterFeatherAttack([0]),
  // 鳴き挟み (10-29): 爪2発 (roosterClawPair) → 鳴いて真上へ → 主人公の上へ寄って止まり光る → 真下へ落ちて踏みつけ (roosterCrowDrop)。
  //   落ちる所は止まる瞬間の主人公の位置。光ってから 0.38 秒＝走って抜けるか、落ちてくる所を弾く。連の組み＝rooster_fight.ts。
  roosterClawPair: {
    id: 'roosterClawPair',
    label: '鳴き挟み・爪',
    kind: 'physical',
    chargeMs: 520,
    activeMs: 130,
    recoveryMs: 680,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 24,
    lungeDistance: 130,
    comboHits: 2,
    comboGapMs: 320,
    sourceBossId: 'inverted-jester',
  },
  roosterCrowDrop: {
    id: 'roosterCrowDrop',
    label: '鳴き挟み・落ちる踏みつけ',
    kind: 'physical',
    chargeMs: ROOSTER_DROP_CHARGE_MS,
    activeMs: 140,
    recoveryMs: 1000,
    parryable: true,
    defense: 'parry',
    range: 110,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard + 6,
    postureDamage: 40,
    strike: { localFromPx: -100, localToPx: 100 },  // 真下へ落ちて踏む (前後同じ幅)
    choreography: 'roosterCrowDrop',
    sourceBossId: 'inverted-jester',
  },
  // ========== 剣舞の役者 (blade-dancer) ==========
  // 設計 (Claude 裁量・たたき台): 主題は「リズム」。 連続弾きの気持ちよさを作るボス。
  //   体幹 90 (全ボス最少) = 弾き切れば一気に崩れる。 攻撃は長い連舞 = リターンとリスクが両方大きい。
  // 円舞連斬: ワルツの3拍子で斬る3連。 全弾きで体幹78 = ほぼ崩し確定の報酬。
  waltzFlurry: {
    id: 'waltzFlurry',
    label: '円舞連斬',
    kind: 'physical',
    chargeMs: 800,
    activeMs: 130,
    recoveryMs: 720,
    parryable: true,
    defense: 'parry',
    range: 150,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 26,
    lungeDistance: 130,
    comboHits: 3,
    comboGapMs: 380,   // ♩♩♩ の等間隔 (3拍子)
    sourceBossId: 'blade-dancer',
  },
  // 旋回突き: 回転からの刺突 = 見切り。 見切れば舞の輪の中へ踏み込める。
  pirouette: {
    id: 'pirouette',
    label: '旋回突き',
    kind: 'physical',
    chargeMs: 750,
    activeMs: 200,
    recoveryMs: 880,
    parryable: false,
    defense: 'evade-counter',
    unblockable: true,   // 裁定C: 旋回突き=弾けない=赤
    range: 180,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 38,
    lungeDistance: 260,
    sourceBossId: 'blade-dancer',
  },
  // 裾払い: 低い回転で裾が床を薙ぐ = 触れない位置へ (evade)。
  hemSweep: {
    id: 'hemSweep',
    label: '裾払い',
    kind: 'physical',
    chargeMs: 700,
    activeMs: 450,
    recoveryMs: 780,
    parryable: false,
    defense: 'evade',
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 30,
    sourceBossId: 'blade-dancer',
    // 2026-10-21: 作者の答えに合わせ外へ50px広げる。帯=[b+85..b+265]・内側85pxを保ち懐へ抜けられる。
    hazardSpawns: [{
      anchor: 'boss', offset: 175, width: 180, telegraphMs: 700, activeMs: 450,
      damage: PLAYER_DAMAGE.standard, postureDamage: 30, hitOnce: true, kind: 'veil',
    }],
  },
  // 終舞・五連: 加速する5連斬 (最終部屋)。 間隔が3拍子より詰まる = リズムの最終試験。
  //   弾き切れば体幹100 = その場で崩し。 5連の後は長い隙 (踊り切った静止)。
  crescendo: {
    id: 'crescendo',
    label: '終舞・五連',
    kind: 'physical',
    chargeMs: 900,
    activeMs: 120,
    recoveryMs: 1050,
    parryable: true,
    defense: 'parry',
    range: 150,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 20,
    lungeDistance: 110,
    comboHits: 5,
    comboGapMs: 300,   // 3拍子より速い = クレッシェンド
    sourceBossId: 'blade-dancer',
  },
  // 剣舞の致命: 幕引きの一閃。
  dancerExecution: {
    id: 'dancerExecution',
    label: '幕引きの一閃',
    kind: 'physical',
    chargeMs: 550,
    activeMs: 220,
    recoveryMs: 850,
    parryable: false,
    defense: 'evade',
    range: 160,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    lungeDistance: 140,
    sourceBossId: 'blade-dancer',
  },

  // ========== 先代の姫 (former-princess・ラスボス) ==========
  // 設計 (Claude 裁量・たたき台): 主題は「鏡」。 君と同じ背格好・同じ術理で戦う先代。
  //   部屋1-2 = 鏡映の決闘 (プレイヤーの技の写し)。 部屋3 = 舞台そのものが牙を剥く
  //   (照明落下/緞帳/終演の鐘)。 全文法の最終試験 + 「君の役の先代」という物語の鏡。
  mirrorSlash: {
    id: 'mirrorSlash',
    label: '鏡映の斬撃',
    kind: 'physical',
    chargeMs: 650,
    activeMs: 140,
    recoveryMs: 560,
    parryable: true,
    defense: 'parry',
    range: 130,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 40,
    lungeDistance: 140,
    sourceBossId: 'former-princess',
  },
  // 鏡映の突き: 君のカウンター突きの写し。 見切れば、 君の技で君が勝つ。
  mirrorThrust: {
    id: 'mirrorThrust',
    label: '鏡映の突き',
    kind: 'physical',
    chargeMs: 800,
    activeMs: 200,
    recoveryMs: 900,
    parryable: false,
    defense: 'evade-counter',
    unblockable: true,   // 裁定C: 鏡映の突き=弾けない=赤
    range: 170,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 38,
    lungeDistance: 260,
    sourceBossId: 'former-princess',
  },
  // 鏡映の円舞: 2連斬 (剣舞で覚えたリズムの再試験・部屋2)。
  mirrorWaltz: {
    id: 'mirrorWaltz',
    label: '鏡映の円舞',
    kind: 'physical',
    chargeMs: 780,
    activeMs: 130,
    recoveryMs: 820,
    parryable: true,
    defense: 'parry',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 28,
    lungeDistance: 120,
    comboHits: 2,
    comboGapMs: 350,
    sourceBossId: 'former-princess',
  },
  // 照明落下: 舞台の照明が3本の光柱で落ちる (部屋3)。 隙間に立つ = 位置取りの最終問題。
  lightsFall: {
    id: 'lightsFall',
    label: '照明落下',
    kind: 'area',
    chargeMs: 1100,
    activeMs: 650,
    recoveryMs: 850,
    parryable: false,
    defense: 'evade',
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 26,
    sourceBossId: 'former-princess',
    // 2026-06-11 公平性修正: 旧3本(隙間2)は壁際から最寄りの隙間まで205px = 回避不能だった。
    //   4本+隙間3 (壁際にも逃げ場が近い) へ再配置。 最遠135px ≤ 移動上限164px。
    hazardSpawns: [
      // ARENA: leftWall 60 / rightWall 900。 安全な隙間 = [180..300] / [430..550] / [680..800]
      { anchor: 'arena', offset: 120, width: 120, telegraphMs: 1100, activeMs: 650,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'light' },
      { anchor: 'arena', offset: 365, width: 130, telegraphMs: 1100, activeMs: 650,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'light' },
      { anchor: 'arena', offset: 615, width: 130, telegraphMs: 1100, activeMs: 650,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'light' },
      { anchor: 'arena', offset: 850, width: 100, telegraphMs: 1100, activeMs: 650,
        damage: PLAYER_DAMAGE.standard, postureDamage: 26, hitOnce: false, rehitIntervalMs: 550, kind: 'light' },
    ],
  },
  // 緞帳薙ぎ: 幕が前方を呑む (部屋3)。 後ろへ引く or 弾幕の外へ。
  curtainSweep: {
    id: 'curtainSweep',
    label: '緞帳薙ぎ',
    kind: 'area',
    chargeMs: 850,
    activeMs: 550,
    recoveryMs: 800,
    parryable: false,
    defense: 'evade',
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 30,
    sourceBossId: 'former-princess',
    // 2026-06-11 公平性修正: 幅320→200 / 帯=[姫+75..姫+275]。 旧値は帯中央から両出口175px =
    //   移動上限140pxを超過。 内側75px = 壁際×間合い上限でも懐へ抜けられる。
    hazardSpawns: [{
      anchor: 'boss', offset: 175, width: 200, telegraphMs: 850, activeMs: 550,
      damage: PLAYER_DAMAGE.standard, postureDamage: 30, hitOnce: true, kind: 'veil',
    }],
  },
  // 終演の鐘: 姫の周囲 (前後) が鳴動する。 安全圏 = 遠間だけ。 間合いの最終問題。
  bellToll: {
    id: 'bellToll',
    label: '終演の鐘',
    kind: 'magic',
    chargeMs: 950,
    activeMs: 450,
    recoveryMs: 900,
    parryable: false,
    defense: 'evade',
    range: 0,
    damage: PLAYER_DAMAGE.standard,
    postureDamage: 32,
    sourceBossId: 'former-princess',
    // 2026-06-11 公平性修正: 帯=[姫±65..姫±295]。 姫の足元 (±65の内側) は鳴動の中心の静寂 =
    //   壁際で外へ逃げられない時は懐へ潜るのが答え (遠間 or 密着の二択)。
    hazardSpawns: [
      { anchor: 'boss', offset: 180, width: 230, telegraphMs: 950, activeMs: 450,
        damage: PLAYER_DAMAGE.standard, postureDamage: 32, hitOnce: true, kind: 'shock' },
      { anchor: 'boss', offset: -180, width: 230, telegraphMs: 950, activeMs: 450,
        damage: PLAYER_DAMAGE.standard, postureDamage: 32, hitOnce: true, kind: 'shock' },
    ],
  },
  // 姫の致命: 静かな幕引き。
  princessExecution: {
    id: 'princessExecution',
    label: '幕',
    kind: 'physical',
    chargeMs: 550,
    activeMs: 240,
    recoveryMs: 900,
    parryable: false,
    defense: 'evade',
    range: 140,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    lungeDistance: 120,
    sourceBossId: 'former-princess',
  },

  // hollow-actor の前の版 (総集編) の致命: 演目のどの役でもない、 がらんどう自身の一礼。
  hollowExecution: {
    id: 'hollowExecution',
    label: 'がらんどうの一礼',
    kind: 'physical',
    chargeMs: 600,
    activeMs: 250,
    recoveryMs: 850,
    parryable: false,
    defense: 'evade',
    range: 150,
    collisionDraft: true,
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    lungeDistance: 130,
    sourceBossId: 'hollow-actor',
  },

  // 終幕の鬨 (鶏の致命・10-29 作り直し): 崩れた主人公の前で胸を張って長く鳴き上げ、舞い上がって真上から踏みしめる。
  //   踏んだまま、もう一度鳴く (戻りの絵)。台本＝src/rooster_vertical.ts ROOSTER_FINALE。落ちる直前に目が赤く光る (弾けない)。
  crowExecution: {
    id: 'crowExecution',
    label: '終幕の鬨',
    kind: 'physical',
    chargeMs: ROOSTER_FINALE_CHARGE_MS,
    activeMs: 160,
    recoveryMs: 1000,
    parryable: false,
    defense: 'evade',
    range: 100,
    strike: { localFromPx: -90, localToPx: 90 },
    choreography: 'roosterCrowFinale',
    collisionDraft: true, // 鶏の他の技と同じ仮の当たり (台本 ROOSTER_FINALE_TIMELINE も active の頭で当たる)
    damage: PLAYER_DAMAGE.finisher,
    bossFinisher: true,
    postureDamage: 30,
    crossingPolicy: 'not-applicable',
    crossingPolicyExemption: 'boss-finisher',
    sourceBossId: 'inverted-jester',
  },
}

// Preserve the pre-adoption data for the DEV comparison lab.
export const AWOKEN_PREVIOUS_RUSH_ATTACK = ATTACKS.feralRush!
ATTACKS.feralRush = awokenRushStudyAttack(AWOKEN_PREVIOUS_RUSH_ATTACK)

// 2026-10-21: DEV の旧幅比較用。内側85pxを保ち、130以外は配布版の180pxへ戻す。
export function setBladeHemWidth(v: string | null): void {
  const spawn = ATTACKS.hemSweep.hazardSpawns![0]
  spawn.width = v === '130' ? 130 : 180
  spawn.offset = 85 + spawn.width / 2
}

export function getAttack(id: string): AttackDef {
  const a = ATTACKS[id]
  if (!a) throw new Error(`attack not found: ${id}`)
  return import.meta.env.DEV ? wallHuntGameAttack(a) : a
}

// 死因表示 (P4): 攻撃ラベルの安全取得。 未知 id でも throw しない (defeat 画面で例外を出さない)。
export function getAttackLabel(id: string): string | undefined {
  return ATTACKS[id]?.label
}
